import {
  createContext,
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cerrarSesion } from '../../lib/cerrarSesion';
import { ChevronRight, Copy, Crop, Expand, Instagram, LayoutGrid, Maximize, MoveRight, Pencil, Play, type LucideIcon } from 'lucide-react';
import { useSidebarColapsable } from './useSidebarColapsable';
import { useTooltipsSidebar } from './TooltipSidebar';
// ≥1024px: el ancho a partir del cual la sidebar se puede desplegar sin comerse el
// contenido (ADR-2026-08-05-el-shell-nuevo-arranca-en-768).
import { EnlaceApp } from '../../components/EnlaceApp';
import { useIsDesktop } from '../../components/onboarding/useIsDesktop';
import { useBrandProfile, type BrandRow } from '../../hooks/useBrandProfile';
import { useNonceDeMarca } from '../../hooks/useNonceDeMarca';
import { marcaParaFiltrar } from '../../utils/filtroDeMarca';
import { getUserPlan, planAllowsMultiBrand } from '../../lib/planUtils';
import { useShellGuards } from '../../hooks/useShellGuards';
import { esRutaShellLegacy } from '../../lib/rutasShellLegacy';
import { useCuentaAtlas } from '../../hooks/useCuentaAtlas';
import { usaInterfazNueva } from '../../lib/userGroups';
import { useRegistrarModoDeRuta } from '../../hooks/useModosRecientes';
import { SeccionesDeLaSidebar } from './SeccionesDeLaSidebar';
import { GruposDeLaSidebar } from './GruposDeLaSidebar';
import { useFuncionesAvanzadas } from '../../hooks/useFuncionesAvanzadas';
import {
  useCreacionesRecientes,
  type CreacionReciente,
} from '../../hooks/useCreacionesRecientes';
import { GeneratedImagesHistory } from '../GeneratedImagesHistory';
import { SidebarContext, useSidebar } from '../../context/SidebarContext';
import { usePermissions } from '../../context/PermissionContext';
import { BotonFlotanteChat, PanelChatAtlas, useEmpujeChat } from '../../chat-atlas';
import { CreditsTopBar } from '../../components/CreditsTopBar';
import { CampanaNotificaciones } from '../../components/notificaciones/CampanaNotificaciones';
import { useCardAyudaSidebar } from '../../components/CardAyudaSidebar';
import { CreditsPricingSheet } from '../../components/pricing/CreditsPricingSheet';
import {
  copyPricingPorMotivo,
  type MotivoPricing,
} from '../../components/pricing/pricingContexts';
import { CAPACIDAD_POR_PLAN, estadoDeCreditos } from '../../lib/creditsStatus';
import { EXPANSION_DISPONIBLE } from '../../lib/expansionMagica';
// De este módulo va quedando cada vez menos, y a propósito:
//  - `CREACIONES_RECIENTES` / `ItemGaleria` salieron con ADR-2026-07-31-shell-nuevo-datos-reales —
//    las creaciones son las del usuario (`useCreacionesRecientes`), no las imágenes
//    de demo del alta.
//  - `elegirFilasPlantillas` salió con ADR-2026-07-31-inicio-se-funde-interfaz-v2 — las
//    plantillas son las colecciones reales que trae `InterfazV2`.
//  - `CARRUSEL_MODOS` y `saludo` salieron con ADR-2026-09-01-la-version-2-es-la-home:
//    las carátulas de la tira y el saludo se los llevó la home a `HomeMinimal.tsx`.
// `TODAS_LAS_HERRAMIENTAS` alimenta la sidebar, que sí es un menú fijo.
import { TODAS_LAS_HERRAMIENTAS, type HerramientaAtlas } from './contenidoAtlas';
import {
  MODES,
  MODE_PATH_TO_KIND,
  modoVisiblePorFlags,
  anuncioTercero,
} from '../v2/modesCatalog';
// `isPaidPlan` ya no se importa acá: las dos secciones que lo usaban —"Creaciones
// recientes" y su fila en el esqueleto— pasaron a `muestraCreacionesRecientes`, que
// además del plan mira el rubro (ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda).
import {
  getModePlanLock,
  puedeUsarElAgente,
  type ModePlanLock,
} from '../../lib/planCapabilities';
import {
  rubroVePlantillas,
  rubroVePlantillasBelleza,
  esRubroBelleza,
  esRubroHogarDeco,
  esRubroModa,
} from '../../lib/rubro';
import { ETIQUETA_SIN_RUBRO } from '../../lib/onboardingPendiente';
import { muestraCreacionesRecientes } from '../../lib/seccionesHome';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';

/**
 * Shell de la plataforma en **desktop** — nacido como "Estilo 3 / Minimal"
 * (referencia: Vidnoz, ADR-2026-07-30-tres-estilos-interfaz-shells-propios) y promovido a interfaz real en
 * ADR-2026-07-31-shell-nuevo-urls-viejas + ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * Shell claro y vacío: sidebar flotante con lo mínimo, saludo de bienvenida, carrusel
 * de modos y el resto del espacio libre.
 *
 * DEJÓ DE SER UN PREVIEW. Ya no vive detrás de `import.meta.env.DEV` ni corre con
 * datos simulados: es el layout de las rutas que ya existían —`/interfaz-v2/*`,
 * `/mi-marca`, `/redes-sociales/imagenes`, `/editor-video`, `/configuracion`— más
 * `/inicio`, que es la única pantalla nueva. Las URLs no cambiaron; cambió el chrome
 * que las envuelve.
 *
 * **Sólo desktop.** En mobile esas mismas rutas siguen montando `DashboardLayout`
 * (Sidebar + MobileBottomNav): lo decide `ShellApp` en `App.tsx` por viewport, con el
 * mismo patrón que `AccountSetupResultsRoute`. Este árbol asume `h-screen` con una
 * sidebar de 244px en flujo, que abajo de 768px se come la pantalla.
 *
 * De lo simulado no queda nada del usuario: identidad, plan, créditos, capacidad,
 * descuento, marca activa y creaciones recientes salen de las mismas tablas que lee
 * el Sidebar de producción. Lo que sigue siendo estático es el CATÁLOGO de producto
 * (modos y plantillas de `contenidoAtlas`), que es contenido, no datos de cuenta.
 *
 * Viene en dos variantes de superficie (ADR-2026-07-30-minimal-gris-variante-superficie-prop), que es lo ÚNICO que cambia entre
 * ellas: mismo layout, mismo contenido, mismo copy. Se resuelve por prop y no
 * duplicando la pantalla porque este estilo se sigue iterando: un clon habría quedado
 * desfasado al primer cambio.
 */

/** Qué superficie es fondo y cuál es figura. Lo único que separa a las dos variantes. */
export type VarianteMinimal = 'claro' | 'gris';

/**
 * Un modo en la tira de la home — ADR-2026-07-31-inicio-se-funde-interfaz-v2.
 *
 * La home ya no arma esta lista: se la pasa `InterfazV2`, que es la que sabe qué
 * modos existen para este usuario (feature flags, rubro) y qué pasa al abrir uno
 * (gating por plan, guardado del scroll, sheet vs navegación). Acá sólo se dibuja.
 */
export interface ModoHome {
  /** `MODES.path` (`auto`, `modelo`, …). Es la clave de la carátula del catálogo. */
  path: string;
  /** Nombre a mostrar si el modo no tiene entrada en `COPY_MODOS`. */
  titulo: string;
  /** Imagen del preset, como respaldo cuando el catálogo no tiene carátula propia. */
  imagen: string;
  /** El plan del usuario no lo incluye: se dibuja el candado. */
  bloqueado: boolean;
  /**
   * El modo bloqueado tiene ejemplos cargados → la card lleva además el pill
   * "Ver ejemplos" y tocarla abre el visor en vez de la pantalla de planes
   * (ADR-2026-08-25-pill-ver-ejemplos-en-cards-bloqueadas, el mismo contrato que
   * `ModoSheet` en el sheet de Generar de mobile). Va como FLAG y no se deduce acá:
   * quién sabe de `getModeExamples` es el llamador, el mismo que en `onAbrir` decide
   * que ese click abre el visor.
   */
  conEjemplos?: boolean;
  /**
   * De qué lado del árbol cuelga el modo: lo que produce es una imagen o un video.
   * Lo decide `InterfazV2` (por `outputType` del preset), igual que el sheet de
   * "Generar" de mobile (`useModosGenerar`), para que las dos superficies partan la
   * lista por el mismo criterio.
   *
   * Opcional: lo lee la home (`HomeMinimal.tsx`), que reparte los modos en las dos
   * cards de vertiente — elegir imagen o video es su primer paso.
   */
  familia?: 'imagen' | 'video';
  /** Qué hacer al clickearlo. Lo resuelve el llamador, con su gating. */
  onAbrir: () => void;
  /**
   * A dónde lleva, para que la card sea un enlace de verdad y Cmd/Ctrl+clic la abra en
   * otra pestaña (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links).
   *
   * Opcional y lo pone el llamador, igual que `onAbrir`: el modo BLOQUEADO no lo trae
   * —su click no va al modo sino a los planes o al visor de ejemplos— y sin `href` la
   * card vuelve a ser un botón, que es lo correcto para algo que no navega.
   */
  href?: string;
}

/**
 * Una fila de plantillas de la home. Una fila = una colección real de las que trae
 * el backend (`CollectionV2`), no una categoría del catálogo estático.
 */
export interface FilaPlantillasHome {
  id: string;
  titulo: string;
  bajada?: string;
  items: {
    id: string;
    src: string;
    titulo: string;
    onAbrir: () => void;
  }[];
  /** "Ver más" de la fila — lleva a la colección completa. */
  onVerMas: () => void;
  /** La URL de esa colección, para que "Ver más" sea un enlace de verdad
   *  (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links). */
  hrefVerMas?: string;
}

// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export interface TemaMinimal {
  /** Fondo de la pantalla. */
  fondo: string;
  /** Sidebar. */
  panel: string;
  /** Bloques de contenido: tarjetas de sección y celdas de imagen. */
  tarjeta: string;
  /** Un escalón por encima de `tarjeta`: chips de ícono y la tarjeta de cuenta. */
  hueco: string;
  /** Celda "Ver más": vacía y punteada, va un tono por debajo del resto. */
  vacio: string;
  /** Fondo del ítem de sidebar seleccionado. Sigue la misma inversión que el panel. */
  seleccion: string;
}

// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export const TEMAS: Record<VarianteMinimal, TemaMinimal> = {
  // Original: lienzo gris, contenido en blanco.
  claro: {
    fondo: 'bg-[#F9F9F9]',
    panel: 'bg-white',
    tarjeta: 'bg-white',
    hueco: 'bg-[#F2F3F5]',
    vacio: 'bg-[#FAFAFB]',
    // Sobre panel blanco el seleccionado necesita color propio: el tinte de marca.
    seleccion: 'bg-[#EBEFFE]',
  },
  // Invertida: lienzo blanco, contenido en gris. Los grises son los mismos de la
  // variante clara, cambiados de rol — no una paleta nueva.
  gris: {
    fondo: 'bg-white',
    panel: 'bg-[#F1F2F4]',
    tarjeta: 'bg-[#F4F5F7]',
    hueco: 'bg-white',
    vacio: 'bg-white',
    // Sobre panel gris alcanza con el blanco: la pastilla resalta por contraste de
    // superficie, sin sumar un color más. El ícono y el texto siguen en azul de marca.
    seleccion: 'bg-white',
  },
};

/**
 * Identidad del usuario — **una sola fuente** (ADR-2026-07-31-avatar-pieza-sola-identidad-sola), ahora REAL
 * (ADR-2026-07-31-shell-nuevo-datos-reales, que supersede el origen del dato). El nombre completo
 * manda: de él salen el nombre del saludo (el primero) y la inicial del avatar, así
 * que cambiar el nombre en un solo lugar sigue actualizando los tres. Lo que cambió
 * es de dónde viene: `profiles.name` vía `useCuentaAtlas`, y ya no una constante.
 *
 * Los fallbacks NO son un nombre de ejemplo. Mientras carga —o si la fila no trae
 * nombre— el saludo va sin nombre y la inicial sale del mail: un "Hola, Maxi" a un
 * usuario que no se llama así es peor que un "Hola" a secas, y era exactamente lo
 * que pasaba con las constantes del preview.
 */

/** Nombre de pila para el saludo. Cadena vacía si todavía no hay nombre. */
// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export function nombrePila(nombreCompleto: string | null): string {
  return nombreCompleto?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Inicial del avatar. Del nombre completo y en mayúscula, sin asumir que ya lo esté:
 * un `name` cargado como "maxi" tiene que seguir dando "M". Si no hay nombre cae al
 * mail, que siempre existe con sesión.
 */
function inicialDe(nombreCompleto: string | null, email: string | null): string {
  const base = nombreCompleto?.trim() || email?.trim() || '';
  return (base[0] ?? '?').toUpperCase();
}

/**
 * Avatar del usuario — ADR-2026-07-31-avatar-pieza-sola-identidad-sola. Tinte de marca de fondo y la inicial en el azul 500,
 * el mismo par que usan el ítem de sidebar seleccionado y el chip de marca.
 *
 * Es un componente y no una clase suelta porque aparece en cuatro lugares (saludo,
 * tarjeta de cuenta, sidebar colapsada y cabecera del menú) y tienen que cambiar
 * juntos: antes el del saludo era un degradé y los otros tres un círculo negro.
 *
 * La inicial la toma de `CuentaContext` y no de una prop: los cuatro avatares son la
 * misma persona, y pasarla por props obligaba a que cada uno de los cuatro llamadores
 * —dos de ellos en la sidebar, uno en el menú, uno en la home— la recibiera y la
 * reenviara.
 */
// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export function AvatarUsuario({
  px = 36,
  className = '',
}: {
  px?: number;
  className?: string;
}) {
  return (
    <span
      style={{ width: px, height: px }}
      className={`grid shrink-0 place-items-center rounded-xl bg-[#F4F4F5] ring-1 ring-black/[0.08] ${className}`}
    >
      <span
        className={`material-symbols-rounded ${GRIS_SHELL}`}
        style={{ fontSize: Math.round(px * 0.58) }}
      >
        person
      </span>
    </span>
  );
}

/**
 * Fallback del selector de marca: **sólo** se ve si no hay sesión o la cuenta no
 * tiene marca ni empresa cargada. Con sesión, el selector muestra la marca activa
 * real y su rubro (ADR-2026-07-31-rubro-selector-sale-marca-real).
 *
 * El rubro es una de las categorías reales del alta y no un string inventado, así
 * el fallback tiene el largo que va a tener el dato de verdad.
 */
const MARCA_EJEMPLO = { nombre: 'Casa Aurora', rubro: 'Hogar y decoración' };

/* Acá vivían `PLAN_DEFAULT` / `CREDITOS_DEFAULT` / `DESCUENTO_DEFAULT` (plan gratis,
   0 créditos, 50% off). Eran el contexto fijo del preview, que corría sin sesión.
   Ahora los tres salen de `useCuentaAtlas` — ADR-2026-07-31-shell-nuevo-datos-reales. El descuento en
   particular NO tiene default: sin cupón vigente la barra no promete ninguno, que es
   el mismo criterio de fallo cerrado que `lib/hotSaleCoupon`. */
/** `company_profiles.plan` → etiqueta, igual que el Sidebar de la app real. */
const ETIQUETA_PLAN: Record<string, string> = {
  gratis: 'Plan Gratis',
  emprendimiento: 'Plan Básico',
  negocio: 'Plan Pro',
  enterprise1: 'Plan Ultra',
  enterprise2: 'Plan Ultra',
  enterprise3: 'Plan Ultra',
};

/* La capacidad por plan —el denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app)— vive en
   `lib/creditsStatus` desde ADR-2026-07-31-contador-sidebar-habla-creditos-sale: estaba copiada acá y en `useHomeBanners`. */

/**
 * Nav de la sidebar. Apunta a las rutas CANÓNICAS de la plataforma
 * (ADR-2026-07-31-shell-nuevo-urls-viejas, que supersede ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo en el destino).
 *
 * Con el shell nuevo aplicado sobre las rutas que ya existían, `/creaciones` y
 * `/editar-marca` dejaron de ser pantallas propias: son alias que redirigen a
 * `/redes-sociales/imagenes` y `/mi-marca`. Si la sidebar siguiera apuntando al
 * alias, cada click haría un redirect visible y —peor— el ítem nunca quedaría
 * marcado, porque la URL final no coincide con la del ítem.
 *
 * "Inicio" apunta a `/interfaz-v2` desde ADR-2026-07-31-inicio-se-funde-interfaz-v2:
 * `/inicio` dejó de ser una pantalla y pasó a ser un alias más. La home real —con el
 * diseño nuevo y toda la lógica— es `/interfaz-v2`.
 */
const NAV_LATERAL = [
  // "Inicio" NO se gatea por `showsV2Studio` aunque el ítem equivalente de la sidebar
  // vieja ("Interfaz V2") sí lo estuviera — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
  // En desktop `HomeRedirect` manda a TODOS a `/interfaz-v2`: este shell ES la
  // experiencia V2, no una de dos opciones. Gatearlo por grupo dejaría a un Grupo A
  // parado justo en la pantalla que la sidebar se niega a listar.
  // "Inicio" sin `descripcion` a propósito: es de los ítems auto-explicativos
  // que no muestran card de ayuda (ADR-2026-09-01-card-ayuda-hover-sidebar).
  { label: 'Inicio', icono: 'home', ruta: '/interfaz-v2' },
  {
    label: 'Creaciones',
    icono: 'photo_library',
    ruta: '/redes-sociales/imagenes',
    permiso: 'imagenes',
    descripcion:
      'Todas las imágenes y videos que generaste, listos para ver y descargar.',
  },
  // Sin `permiso`: en la sidebar vieja "Mi marca" es el único `alwaysShow` — no es
  // una feature gateada.
  {
    label: 'Marca',
    icono: 'shopping_bag',
    ruta: '/mi-marca',
    descripcion:
      'Tus productos y la información de tu marca, la base de todo lo que generás.',
  },
  // El Agente. Dejó de ser un acceso interno —
  // ADR-2026-09-14-el-agente-es-de-pro-y-ultra: vivía en `ACCESOS_INTERNOS`, o sea que
  // para un cliente su única puerta era el flotante, apagado por defecto. Ahora está a la
  // vista de quien lo tiene.
  //
  // `soloProUltra` y no `permiso`: la clave `chat-atlas` que usaba de acceso interno está
  // en `true` por defecto (el corte lo daba `dev_tester`), así que no gatea nada. El piso
  // real es el plan y sale de `puedeUsarElAgente`, el mismo que consumen el flotante y la
  // card de mobile.
  //
  // SIN candado, al revés que la card del sheet de Generar: esta nav no tiene ese
  // vocabulario —sus ítems están o no están— y un ítem trabado sería el único de la lista.
  // Quien no llega lo ve donde se venden los planes.
  {
    label: 'Agente',
    icono: 'forum',
    ruta: '/agente',
    soloProUltra: true,
    descripcion:
      'Tu agente de IA: pedile ideas, análisis, imágenes, videos y acciones sobre tu marca.',
  },
  // Solo si la empresa prendió la función en Configuración → Funciones avanzadas
  // (`funcionAvanzada`, filtrado abajo). Es el primer ítem opcional de esta lista:
  // los otros gates existentes son por permiso (inerte) o por dev_tester.
  {
    // "Tienda online" y no "Lanzamientos": el nombre de adentro (un lanzamiento) es
    // nuestro, pero la persona tiene que reconocer desde el menú que acá se llega a
    // su tienda. Sólo cambia el rótulo: la ruta y el resto siguen siendo "lanzamientos".
    label: 'Tienda online',
    icono: 'rocket_launch',
    ruta: '/lanzamientos',
    funcionAvanzada: 'pre_lanzamientos',
    descripcion:
      'Preparás varios productos con sus fotos y los publicás todos juntos en tu tienda.',
  },
  // Abajo de la tienda y con el mismo flag: antes era un destino más de "Nuevo
  // lanzamiento"; ahora entra directo al calendario de la marca
  // (ADR-2026-10-06-instagram-tiene-su-propia-seccion).
  {
    label: 'Instagram',
    icono: 'calendar_month',
    ruta: '/instagram',
    funcionAvanzada: 'pre_lanzamientos',
    descripcion: 'Tu calendario de posts: qué sube, con qué texto y a qué hora.',
  },
] as const satisfies readonly {
  label: string;
  icono: string;
  ruta: string;
  permiso?: string;
  /** Llave de `company_profiles.funciones_avanzadas` que tiene que estar en true. */
  funcionAvanzada?: string;
  /** Piso de plan Pro/Ultra (`puedeUsarElAgente`). Mismo criterio de "mientras no se
   *  sabe, no se muestra" que `funcionAvanzada`. */
  soloProUltra?: boolean;
  /** Card flotante de ayuda al hover sostenido (ver CardAyudaSidebar). */
  descripcion?: string;
}[];

/**
 * Los accesos que tenía la sidebar VIEJA y a ésta le faltaban —
 * ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
 *
 * Son los nueve ítems de `FLAT_MENU_ITEMS` que no tienen equivalente acá: el Estudio
 * de marketing entero, los dos de "Empleado IA" y Test V1. Sin ellos, borrar la
 * sidebar vieja se llevaba puesto el único acceso a esas pantallas.
 *
 * **La lista entera es `devOnly`**, igual que en la sidebar vieja, así que no hace
 * falta un campo por ítem: se filtra de una (`permiso` + `dev_tester`). Un cliente no
 * ve nada de esto.
 *
 * NO está "Estudio Virtual" (el flujo clásico, `/redes-sociales/crear-anuncio`), que
 * en la vieja sí está y encima lo ve casi todo el mundo (`showsClassicStudio` da
 * `true` para Grupo A **y para grupo desconocido**, que es la mayoría de las cuentas
 * reales). Se deja afuera a propósito: esta sidebar nunca lo listó, en desktop
 * `HomeRedirect` ya manda a todos a `/interfaz-v2`, y agregarlo ahora sería
 * reintroducir el flujo clásico en escritorio para casi todos los clientes — un
 * cambio de producto, no la paridad de accesos que se estaba buscando.
 */
type AccesoInterno = {
  label: string;
  icono: string;
  ruta: string;
  /** La MISMA clave de `PermissionContext` que usa el `permissionKey` de la vieja. */
  permiso: string;
  /** Rótulo del grupo que abre este ítem (se dibuja arriba de él). */
  abreGrupo?: string;
  /**
   * Rutas extra que tienen que dejarlo marcado. Para los ítems que son la puerta de
   * una pantalla con pestañas, donde cada pestaña es su propia URL: sin esto el ítem
   * se apaga al cambiar de pestaña. La ruta propia no se repite acá.
   */
  rutasActivas?: string[];
  /** Card flotante de ayuda al hover sostenido (ver CardAyudaSidebar). El MISMO
   *  texto que el ítem equivalente de la sidebar vieja. */
  descripcion?: string;
};

const ACCESOS_INTERNOS: AccesoInterno[] = [
  {
    label: 'Mapa',
    icono: 'hub',
    ruta: '/estudio-marketing/mapa',
    permiso: 'em-mapa',
    abreGrupo: 'Estudio de marketing',
    descripcion:
      'La vista general de tu marketing: fuentes, análisis y acciones conectados en un mapa.',
  },
  {
    label: 'Plan',
    icono: 'checklist',
    ruta: '/estudio-marketing/plan',
    permiso: 'em-plan',
    descripcion:
      'Tu plan de marketing: tareas y acciones concretas sugeridas a partir de tus datos.',
  },
  {
    // Insights y Estrategia se fusionaron en una pantalla de dos pestañas, pero cada
    // pestaña sigue siendo su URL de siempre (ADR-2026-08-17-analisis-datos-tabs-por-ruta).
    label: 'Análisis de datos',
    icono: 'insights',
    ruta: '/estudio-marketing/insights',
    permiso: 'em-insights',
    rutasActivas: ['/estudio-marketing/estrategia'],
    descripcion:
      'Insights y estrategia generados a partir de los datos de tus canales.',
  },
  {
    label: 'Marca',
    icono: 'palette',
    ruta: '/estudio-marketing/marca',
    permiso: 'em-marca',
    descripcion:
      'La identidad de tu marca: estilo, voz y lineamientos que guían todo el estudio.',
  },
  {
    label: 'Datos',
    icono: 'database',
    ruta: '/estudio-marketing/datos',
    permiso: 'em-datos',
    descripcion:
      'Conectá y administrá las fuentes de datos que alimentan el estudio de marketing.',
  },
  {
    label: 'Puesta en marcha',
    icono: 'rocket_launch',
    ruta: '/estudio-marketing/onboarding',
    permiso: 'em-onboarding',
    descripcion:
      'El flujo de configuración inicial del estudio de marketing, paso a paso.',
  },
  // El Agente se fue de esta lista a `NAV_LATERAL`
  // (ADR-2026-09-14-el-agente-es-de-pro-y-ultra): dejó de ser interno y su piso pasó a ser
  // el plan. Con él se fue el que abría el grupo "Empleado IA", que pasó al de abajo.
  //
  // OJO: `/redes-sociales/calendario` y `/test-v1` quedaron FUERA de la mudanza al
  // shell (ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas: se migró
  // A + B + E, no D), así que estos dos ítems TODAVÍA saltan al chrome viejo. Se
  // listan igual porque son `devOnly` —sólo los ve el equipo— y sin ellos no hay
  // ninguna otra puerta a esas pantallas desde escritorio.
  {
    label: 'Manager de Redes IA',
    icono: 'smart_toy',
    ruta: '/redes-sociales/calendario',
    permiso: 'calendario',
    abreGrupo: 'Empleado IA',
    descripcion:
      'Calendario de publicaciones para tus redes, planificado y gestionado por IA.',
  },
  {
    label: 'Test V1',
    icono: 'science',
    ruta: '/test-v1',
    permiso: 'test-v1',
    abreGrupo: 'Interno',
    descripcion: 'Herramienta interna para probar el flujo de generación V1.',
  },
];

/**
 * Salida del fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja, afinado en ADR-2026-07-31-ajustes-shell-padding-secciones-hospedadas). El fade es
 * **asimétrico** a propósito: la salida es corta —lo que ya no sirve tiene que
 * irse rápido, si no el click se siente trabado— y la entrada larga (680ms, en la
 * clase del wrapper) para que la sección nueva aparezca de a poco.
 *
 * Duplicado respecto de los valores originales (120/340) a pedido: el fade tenía
 * que sentirse más sutil todavía.
 *
 * Sólo la salida es una constante: la entrada la aplica Tailwind y sus clases
 * tienen que ser literales para que el JIT las genere, así que interpolarla acá
 * no serviría.
 */
const FADE_SALIDA_MS = 240;

/**
 * Estado seleccionado de un ítem de sidebar: ícono y texto en el azul 500 de la marca
 * (`brand.500` / `--brand-primary`, #1644EB). El fondo lo pone el tema (`seleccion`),
 * que cambia con la variante.
 */
const ITEM_ACTIVO = 'text-brand-primary';

/**
 * El gris del shell: negro al 60%. Lo usan los ítems NO seleccionados de las dos
 * listas de la sidebar (ícono y etiqueta), los ítems del pie, los controles de arriba
 * a la derecha y los "Ver todo" de las secciones.
 *
 * Antes cada uno tenía el suyo —70% el texto de la nav, 65% el pie y la pastilla de
 * ayuda, 55% el ícono del avatar— y encima los íconos de la nav sumaban un
 * `opacity-75` propio, así que terminaban al 52,5%: seis grises apenas distintos
 * que se leían como un desajuste y no como jerarquía. Un solo valor.
 */
// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export const GRIS_SHELL = 'text-black/60';

/**
 * "Ver todo" de las secciones de la home. Volvió a ser texto —sin caja, sin borde—
 * pero en bold: pegado al título (16px) ya se lee como parte de esa fila, así que la
 * caja lo único que hacía era competir con el título en peso visual. El bold es lo
 * que lo sostiene como control.
 *
 * Sin padding horizontal: con la caja, el `px-3` era parte del botón; sin ella,
 * separaba el texto del título más que los 16px que pide el layout.
 */
const BOTON_VER_TODO =
  'mb-1 flex shrink-0 items-center gap-1.5 text-[13px] font-bold text-brand-primary transition-colors hover:text-brand-primary-hover';

/* Acá vivía `MODO_APAGADO`, la tabla de modos escondidos por feature flag
   (ADR-2026-07-31-sidebar-filtra-modos-apagados). Tenía una sola entrada,
   `catalogo-muebles`, y el flag que la alimentaba (`CATALOGO_MUEBLES_ENABLED`)
   se eliminó al mergear `principal`: ese modo y su hermana Infografía ya no se
   apagan globalmente sino que dependen del RUBRO de la marca activa
   (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).

   Los dos se filtran ahora abajo, en `herramientasOrdenadas`, junto a Plantillas —
   los tres ítems de la sidebar que dependen del rubro y no de una constante de
   módulo. */

/**
 * Herramientas de la sidebar, con **Anuncio tercero**
 * (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar): en el orden de `MODES` cae último,
 * detrás de los dos de catálogo y del asistente, y es de las cosas que más se usan.
 * La lista y la tira comparten la posición, así que el modo está en el mismo lugar se
 * lo busque donde se lo busque.
 *
 * Acá NO está el lugar de "Plantillas": ese depende del plan, así que lo resuelve
 * `ordenarHerramientas` más abajo. Se reordena en este módulo y no en
 * `TODAS_LAS_HERRAMIENTAS` para no mover la fila de íconos del estilo Workspace, que
 * la consume por índice.
 */
const HERRAMIENTAS_SIDEBAR = anuncioTercero(TODAS_LAS_HERRAMIENTAS, (h) => h.id);

/**
 * Orden final de la sidebar — ADR-2026-08-03-plantillas-cierra-los-disponibles.
 *
 * Tres bloques: los modos que el plan habilita, **Plantillas cerrando ese bloque**, y
 * los bloqueados al final.
 *
 * "Plantillas" no es un modo de generación: es un atajo al catálogo. Por eso venía
 * último de la lista entera — pero con los bloqueados abajo, "último" pasó a
 * significar "después de lo que no podés usar", y quedaba separada de las
 * herramientas con las que sí se trabaja. Cierra lo usable, que es de lo que forma
 * parte.
 *
 * Los bloqueados se mandan al fondo conservando su orden relativo: lo que el plan no
 * incluye es una oferta, no navegación, y no tiene por qué partir la lista al medio.
 */
function ordenarHerramientas(
  herramientas: HerramientaAtlas[],
  estaBloqueada: (h: HerramientaAtlas) => boolean,
): HerramientaAtlas[] {
  const disponibles = herramientas.filter((h) => h.id !== 'plantillas' && !estaBloqueada(h));
  const plantillas = herramientas.filter((h) => h.id === 'plantillas');
  const bloqueadas = herramientas.filter((h) => h.id !== 'plantillas' && estaBloqueada(h));
  return [...disponibles, ...plantillas, ...bloqueadas];
}

/**
 * El historial, en su ruta canónica — ADR-2026-07-31-shell-nuevo-urls-viejas, supersede
 * ADR-2026-07-31-fail-closed-real-catalogo-enlaces.
 *
 * Ese ADR apuntó los enlaces de la home a `/creaciones` porque `/redes-sociales/imagenes`
 * vivía sólo bajo `DashboardLayout` y sacaba al usuario del shell nuevo a mitad de
 * flujo. Ahora `/redes-sociales/imagenes` ES la ruta hospedada, y `/creaciones` el
 * alias que redirige a ella: apuntar al alias sólo agregaría un salto.
 */
const RUTA_HISTORIAL = '/redes-sociales/imagenes';

/**
 * El perfil, por el mismo motivo que `RUTA_HISTORIAL`: lo enlazan la tarjeta de
 * cuenta y el menú del avatar, y los dos tienen que coincidir con lo que marca la
 * sidebar.
 *
 * `RUTA_PLANTILLAS` vivía acá y se fue con ADR-2026-07-31-inicio-se-funde-interfaz-v2:
 * el "Ver todo" de plantillas ahora lo resuelve el llamador, que es el mismo que
 * decide qué filas se muestran.
 */
const RUTA_PERFIL = '/configuracion';

/**
 * Ancho de la sidebar flotante, y el margen que la separa del borde y del contenido
 * (`m-2` = 8px). Se nombran porque además del `<aside>` los consume el override de
 * `SidebarContext` (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell): las pantallas hospedadas anclan paneles `fixed` a
 * `sidebarWidth`, y ese número tiene que ser el borde DERECHO de esta sidebar
 * —margen + ancho + margen— y no el ancho a secas.
 */
const ANCHO_SIDEBAR = 244;
const ANCHO_SIDEBAR_COLAPSADA = 66;
// Mínima: la sidebar se cierra a ANCHO CERO (animado) y queda sólo el logo, un botón
// aparte que flota en la esquina y la devuelve
// (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira). El lugar que ese logo se
// reserva lo publica `ANCHO_LOGO_SIDEBAR_MINIMA`, en el contexto.
const MARGEN_SIDEBAR = 8;

/* El motivo por el que se abre el catálogo (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo) y su copy viven en
   `pricingContexts` desde ADR-2026-07-31-copy-catalogo-sigue-saldo-real: la tabla estaba duplicada acá y en
   `SubscriptionLimitModal`, y agregar un motivo arreglaba uno solo. */

/**
 * Abre el catálogo como overlay. Lo provee el shell para que lo consuma cualquier
 * descendiente, incluidas las secciones montadas en el `<Outlet />`: el estado del
 * overlay tiene que ser del shell, porque si viviera en la sección cambiar de
 * pantalla lo cerraría.
 *
 * Lleva además el **estado de cuenta** (plan y créditos) porque lo consume la misma
 * gente que la acción: el encabezado de la home muestra los créditos al lado del CTA
 * de mejora (ADR-2026-07-31-creditos-mejora-plan-encabezado-home), y esos números viven
 * en el shell. Pasarlos por props obligaría a que cada sección hospedada los reciba y
 * los reenvíe.
 *
 * Los defaults son inertes para que un consumidor montado fuera del shell no explote.
 */
interface CuentaCtx {
  abrirPricing: (motivo?: MotivoPricing) => void;
  creditos: number;
  planLabel: string;
  /**
   * `true` cuando no hay nada que resolver: plan pago **y** saldo por encima del
   * umbral. Es el criterio que decide si el CTA de mejora va sólido o en tinte
   * (ADR-2026-07-31-columna-contenido-90-mejorar-plan); se calcula una vez en el shell para que los dos botones —el de la
   * sidebar y el del encabezado— no puedan discrepar.
   */
  planAlDia: boolean;
  /* Identidad real del usuario — ADR-2026-07-31-shell-nuevo-datos-reales. Viaja por el contexto
     y no por props por lo mismo que el plan: la consumen los cuatro avatares, la
     cabecera del menú de cuenta y el saludo de la home, repartidos entre el shell
     y la sección. */
  /** `profiles.name`, o `null` mientras carga / si la fila no lo trae. */
  nombreCompleto: string | null;
  /** `profiles.email`, con el de la sesión como respaldo. */
  email: string | null;
  /** Inicial del avatar, ya derivada y en mayúscula (ver `inicialDe`). */
  inicial: string;
  /** Empresa del usuario — la home la necesita para sus creaciones recientes. */
  idCompany: string | null;
  /**
   * Marca activa (`brand_profiles.id`) — ADR-2026-08-03-creaciones-recientes-por-marca.
   * La consumen las secciones que muestran contenido generado, que es contenido DE
   * una marca y no de la cuenta. `null` mientras resuelve.
   */
  idMarca: string | null;
  /**
   * Rubro de la marca activa (`brand_profiles.rubro`) —
   * ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
   *
   * Va en el contexto y no como prop porque quien lo necesita es la TIRA DE MODOS, tres
   * niveles abajo, y sólo para elegir una carátula: enhebrarlo por props obligaba a
   * `HomeMinimal` —que no decide nada del rubro— a recibirlo y pasarlo. El shell ya lo
   * tiene resuelto para filtrar la sidebar, así que es el mismo valor y no una segunda
   * fuente. `null` mientras carga.
   */
  rubroMarca: string | null;
  /**
   * `true` sólo cuando el valor lo provee ESTE shell — ADR-2026-07-31-shell-provee-flag-hospedaje.
   *
   * Lo consultan las pantallas hospedadas que traen su propio chrome duplicado: la
   * barra de créditos y el panel CONTEXTO DEV los pinta el shell, así que
   * `InterfazV2` tiene que callarse los suyos o quedan dos.
   *
   * Es un flag de contexto y no una comparación de `pathname` ni un `useIsDesktop()`
   * a propósito: la pregunta real es "¿quién me montó?", y las dos aproximaciones se
   * desincronizan del router en cuanto se mueve una ruta. Justamente eso pasó con la
   * versión anterior (`pathname.startsWith('/modo/')`), que quedó siempre `false` al
   * pasar el shell a las URLs viejas (ADR-2026-07-31-shell-nuevo-urls-viejas).
   */
  dentroDelShell: boolean;
  /**
   * Corre `accion` con el MISMO fade de salida que usa la sidebar para cambiar de
   * sección (ADR-2026-08-03-tira-modos-usa-fade-del-shell): apaga el contenido,
   * espera `FADE_SALIDA_MS` y recién ahí ejecuta.
   *
   * Existe porque la tira de modos de la home no navega por su cuenta: el destino lo
   * arma `InterfazV2` (gating de plan → visor de ejemplos, guardado del scroll), así
   * que el shell no puede interceptar una ruta. Lo que se comparte es la TRANSICIÓN,
   * no el destino.
   *
   * Fuera del shell es identidad: se ejecuta en el acto, sin fade.
   */
  conFadeDeSalida: (accion: () => void) => void;
  /**
   * `true` mientras `useCuentaAtlas` no resolvió — ADR-2026-08-03-home-aparece-entera-o-skeleton.
   *
   * Todo lo demás de este contexto ya tiene un valor mientras carga (0 créditos,
   * nombre `null`), que es lo correcto para la sidebar —muestra el hueco y se
   * completa— pero no para la home, que necesita saber si esperar.
   */
  cargandoCuenta: boolean;
  /** Dueño de la empresa (`company_profiles.owner_user_id`). Gatea "Suscripción" en el menú
   *  (spec 2026-09-11-invitaciones-equipo §4.3). */
  esDueno: boolean;
  /**
   * La interfaz nueva —la home por destino y la sidebar por secciones— la ve sólo el
   * grupo C (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva). Se calcula UNA vez acá, del
   * mismo `grupo_testing` que trae `useCuentaAtlas`, para que la home, la sidebar y las
   * pantallas de plantillas no puedan discrepar. Desconocido → `false` (la UI de siempre).
   */
  uiNueva: boolean;
}

const CuentaContext = createContext<CuentaCtx>({
  abrirPricing: () => {},
  creditos: 0,
  planLabel: '',
  planAlDia: false,
  nombreCompleto: null,
  email: null,
  inicial: '?',
  idCompany: null,
  idMarca: null,
  rubroMarca: null,
  // Default `false`: un consumidor montado fuera del shell —InterfazV2 en mobile,
  // bajo `DashboardLayout`— tiene que seguir pintando su propio chrome.
  dentroDelShell: false,
  // Sin shell no hay nada que apagar: la acción corre tal cual.
  conFadeDeSalida: (accion) => accion(),
  // Sin shell no hay cuenta que esperar: el consumidor dibuja con lo que tenga.
  cargandoCuenta: false,
  esDueno: false,
  // Sin shell (o sin cuenta todavía) no hay grupo: la interfaz de siempre.
  uiNueva: false,
});

/**
 * Estado de cuenta y hospedaje, para las pantallas montadas en el `<Outlet />`.
 * Se exporta el hook y no el contexto: los consumidores no tienen por qué saber que
 * esto es un `createContext`, y así el default inerte no se puede saltear.
 */
export function useCuentaShell(): CuentaCtx {
  return useContext(CuentaContext);
}

/**
 * Clases del CTA "Mejorar plan" según ese criterio. Sólido cuando hay algo que
 * resolver; en tinte azul con texto de marca cuando no —a un cliente pago con
 * créditos de sobra el botón no le está pidiendo nada—.
 */
function clasesMejorarPlan(planAlDia: boolean): string {
  return planAlDia
    ? 'bg-[#EBEFFE] text-brand-primary hover:bg-[#DFE6FD]'
    : 'bg-brand-primary text-white hover:bg-brand-primary-hover';
}

/**
 * Mensaje que precarga el link directo del modal de soporte —
 * ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio.
 *
 * Acá vivía `WHATSAPP_URL = 'https://wa.me/5491122635392'`, la TERCERA copia del número
 * (las otras dos: `components/ContactarPorWhatsApp.tsx` y `pages/Ayuda.tsx`). Se fue: el
 * número lo arma ahora `urlsWhatsApp()`, en un solo lugar.
 *
 * Genérico a propósito, aunque este mensaje SÍ podría llevar datos del cliente (el link
 * directo va derecho a WhatsApp, sin intermediarios): el botón es "hablá con soporte"
 * sin ningún contexto de qué le pasa al usuario, así que no hay nada que precargar.
 */


/**
 * "Ediciones rápidas": las acciones que hoy aparecen al abrir una imagen recién
 * generada (la barra de `GeneratedImagesInline`). Mismos íconos —los lucide que usa
 * esa barra— y mismos títulos; la descripción se agrega acá, que en la barra no entra.
 *
 * Las rutas apuntan a donde vive de verdad cada acción: las que necesitan una imagen
 * abierta llevan al historial, que es donde se abre.
 */
export const EDICIONES_RAPIDAS = [
  {
    label: 'Editar',
    desc: 'Cambiá lo que quieras con un prompt',
    Icono: Pencil,
    // A la pantalla de atajos, que resuelve justamente lo que este comentario decía
    // que faltaba — ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
    // Acá decía: "`/redes-sociales/editar-imagen` necesita una imagen abierta que desde
    // acá no existe, así que se va al historial". Ahora el paso de elegir la imagen
    // existe, y una vez elegida sí se puede ir derecho a esa pantalla (que además ya
    // vive en el shell nuevo).
    ruta: '/atajo/editar',
  },
  // Carrusel y Animar SÍ tienen pantalla propia, y ahora el atajo lleva ahí —
  // ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
  //
  // Iban al historial como el resto porque cuando se escribió esta lista eran botones
  // escondidos adentro del visor. Desde ADR-2026-08-14 son modos con pantalla propia
  // que ARRANCAN justo en el paso de elegir imagen (subida o generada), que es
  // exactamente lo que el atajo quiere: mandar al historial era un rodeo que además
  // dejaba al usuario adivinando qué hacer al llegar.
  {
    label: 'Carrusel',
    desc: 'Más tomas del mismo producto',
    Icono: Copy,
    ruta: '/carrusel',
  },
  {
    label: 'Animar',
    desc: 'Convertí la foto en video',
    Icono: Play,
    ruta: '/animar-imagen',
    // Pro/Ultra, igual que su card en la tira y su ítem en la sidebar
    // (`getModePlanLock('animar-imagen', plan)`). Sin esto el atajo era la única puerta
    // al modo SIN candado: se entraba, se elegía la imagen, se escribía el prompt, y el
    // bloqueo aparecía recién al generar (`AnimarImagenMode` sólo frena ahí).
    kindParaCandado: 'animar-imagen',
  },
  {
    label: 'Descripción',
    desc: 'El texto listo para el post',
    Icono: Instagram,
    ruta: '/atajo/descripcion',
  },
  { label: 'Recortar', desc: 'Ajustá el encuadre', Icono: Crop, ruta: '/atajo/recortar' },
  // "Expandir" sólo existe contra atlas-backend (el modo `expandir` no está en el
  // webhook viejo): con el flag apagado el atajo no se muestra — mismo gate que el
  // ítem del menú del visor (EXPANSION_DISPONIBLE). Va al lado de Recortar porque
  // son los dos gestos sobre el encuadre, uno para cada lado —
  // ADR-2026-08-25-expansion-magica-atajo-home.
  ...(EXPANSION_DISPONIBLE
    ? [
        {
          label: 'Expandir',
          desc: 'Llevá la foto a otro formato',
          Icono: Expand,
          ruta: '/atajo/expansion',
        },
      ]
    : []),
  {
    label: 'Mejorar',
    desc: 'Subí la resolución con IA',
    Icono: Maximize,
    ruta: '/atajo/mejorar',
  },
] satisfies readonly {
  label: string;
  desc: string;
  /** El componente de ícono, tal cual lo exporta lucide. Se tipa `LucideIcon` y no un
   *  `ComponentType<{className}>` genérico: lucide devuelve `ForwardRefExoticComponent`,
   *  que NO es asignable a `ComponentType`, y con el tipo genérico el `satisfies`
   *  quedaba en error — o sea inerte, sin validar nada. */
  Icono: LucideIcon;
  ruta: string;
  /**
   * `kind` de `MODES` con el que se pide el candado por plan. Sólo lo declaran los
   * atajos que son la puerta de un modo gateado — hoy Animar.
   */
  kindParaCandado?: string;
}[];

/**
 * El candado de un atajo, o `null` — ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
 *
 * Existe para que los DOS consumidores de `EDICIONES_RAPIDAS` —la sección de la home
 * (`SeccionesHome`) y el sheet de Generar de mobile (`useModosGenerar`)— resuelvan el
 * bloqueo con la MISMA regla. Están en archivos distintos y ninguno ve al otro: sin un
 * punto común, el mismo atajo terminaba abriendo en una superficie y frenando en la
 * otra.
 */
export function candadoEdicionRapida(
  edicion: { kindParaCandado?: string },
  plan: string | null | undefined,
): ModePlanLock | null {
  return edicion.kindParaCandado
    ? getModePlanLock(edicion.kindParaCandado, plan)
    : null;
}

/**
 * Miniaturas de creaciones y plantillas: en MOBILE una sola fila con scroll
 * horizontal; de `sm` para arriba, la grilla de siempre
 * (ADR-2026-07-31-home-mobile-filas-horizontales-ediciones-compactas).
 *
 * En un teléfono la grilla de dos columnas obligaba a scrollear media pantalla por
 * sección para pasar de largo seis miniaturas; en fila, cada sección ocupa el alto
 * de UNA miniatura y el resto se recorre de costado, que es lo que ya hace el
 * usuario en el resto de la app.
 *
 * El `-mx-4 px-4` la lleva a sangre: la fila arranca alineada con el título pero
 * sigue hasta el borde de la TARJETA, así se ve que hay más a la derecha. Se anula
 * en `sm:` porque ahí el contenedor ya no tiene ese padding.
 *
 * Los 4 (16px) son exactamente el `p-4` de la tarjeta que la contiene, y de eso
 * depende que las miniaturas no se salgan: el margen negativo define hasta dónde
 * llega la caja que RECORTA el scroll horizontal. Con `-mx-5` (20px) esa caja
 * quedaba 4px más ancha que la tarjeta de cada lado y, al scrollear, las imágenes se
 * dibujaban afuera del borde redondeado (ADR-2026-08-04-la-fila-se-recorta-en-la-tarjeta).
 */
const FILA_MINIATURAS =
  'scrollbar-hide -mx-4 flex gap-3 overflow-x-auto px-4 sm:mx-0 sm:grid sm:gap-4 sm:overflow-visible sm:px-0 sm:grid-cols-3 xl:grid-cols-6';

/** Ancho fijo de cada miniatura mientras es una fila; en grilla lo da la columna. */
const CELDA_MINIATURA = 'w-[132px] shrink-0 sm:w-auto';

/**
 * Fila de "Creaciones recientes" — ADR-2026-08-03-creaciones-recientes-respetan-su-forma.
 *
 * A diferencia de `FILA_MINIATURAS` (que es una GRILLA de columnas iguales, la que
 * usan las plantillas), acá cada miniatura mide distinto: el alto es el mismo para
 * todas y el ancho lo pone la proporción de cada imagen. Con columnas iguales eso no
 * se puede expresar, así que es una fila flex.
 *
 * En DESKTOP no scrollea: se muestran las que entran y "Ver más" se estira para
 * llenar lo que sobra (ADR-2026-08-03-creaciones-entran-las-que-entran). En mobile sí,
 * que es el patrón esperado ahí y donde a lo sumo entraría una miniatura.
 */
const FILA_CREACIONES =
  'scrollbar-hide -mx-5 flex gap-3 overflow-x-auto px-5 sm:-mx-8 sm:gap-4 sm:overflow-x-visible sm:px-8';

/**
 * Alto de las miniaturas de Creaciones. Es el ÚNICO valor fijo de la celda: el ancho
 * sale de multiplicarlo por la proporción de cada imagen.
 *
 * Los 176px de mobile son exactamente lo que medían antes (132px de ancho por la
 * proporción 3:4 con la que se recortaban), así que esa fila no cambia de alto.
 *
 * ⚠️ `ALTO_CREACION_PX` tiene que seguir a la clase de `sm:`: el cálculo de cuántas
 * miniaturas entran multiplica ESE número por cada proporción. Si se cambia una y no
 * la otra, la cuenta da distinto de lo que se dibuja.
 */
const ALTO_CREACION = 'h-[176px] sm:h-[300px]';
const ALTO_CREACION_PX = 300;

/** Idem: tiene que seguir al `sm:gap-4` de `FILA_CREACIONES`. */
const GAP_CREACION_PX = 16;

/**
 * Ancho mínimo de la celda "Ver más". Se estira para ocupar lo que sobre, pero nunca
 * baja de acá: es lo que se le reserva al calcular cuántas miniaturas entran, y por
 * debajo el círculo y la etiqueta se apretarían.
 */
const MIN_VER_MAS_PX = 132;

/**
 * Proporción con la que se dibuja una miniatura mientras no se sabe la suya: es el
 * formato más común de la base (4:5, seis de cada diez). Se usa en los esqueletos de
 * carga y en las filas sin `formato`, hasta que la imagen carga y se mide sola.
 */
const RATIO_CREACION_DEFECTO = 4 / 5;

/**
 * Cuántas miniaturas entran en `ancho` sin desbordar, dejándole a "Ver más" su mínimo
 * — ADR-2026-08-03-creaciones-entran-las-que-entran.
 *
 * Se recorren en orden y se van sumando: cada una ocupa su ancho (alto × proporción)
 * más el gap que la separa de la anterior. La primera que no entra corta la lista.
 *
 * `reserva` es el lugar que hay que dejarle a "Ver más". Se pregunta DOS veces
 * (ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas): primero con `0`, para saber si entran
 * todas sin esa celda —y entonces no hace falta mostrarla—, y sólo si no entran, otra
 * vez reservándole su mínimo.
 *
 * Devuelve al menos 1: una fila con sólo "Ver más" no dice nada. Si esa única
 * miniatura no entrara —una ventana absurdamente angosta—, `Ver más` se queda en su
 * mínimo y la fila desborda; es preferible a no mostrar ninguna creación.
 */
function cuantasCreacionesEntran(
  ancho: number,
  ratios: number[],
  reserva: number,
  alto = ALTO_CREACION_PX,
  gap = GAP_CREACION_PX,
): number {
  let usado = reserva;
  let n = 0;
  for (const ratio of ratios) {
    const costo = alto * ratio + gap;
    if (usado + costo > ancho) break;
    usado += costo;
    n += 1;
  }
  return Math.max(1, n);
}

/**
 * Ítems del menú de cuenta — ADR-2026-07-31-menu-cuenta-avatar-uno-solo. "Cerrar sesión" NO está acá: va aparte, detrás
 * del divisor, porque es la única acción destructiva del menú.
 *
 * "Centro de ayuda" va a `/ayuda`, igual que el ítem homónimo de la sidebar: son la
 * misma etiqueta y tienen que llevar al mismo lado. El contacto directo por WhatsApp
 * quedó como su propio control ("Whatsapp", en el encabezado), con su propio nombre.
 */
const ITEMS_MENU_CUENTA: {
  label: string;
  icono: string;
  ruta?: string;
  url?: string;
}[] = [
  // "Configuración" y no "Perfil": el destino (`/configuracion`) es la pantalla de
  // ajustes entera —cuenta, plan, marcas, idioma—, no una ficha de perfil, y desde
  // ADR-2026-08-04-configuracion-abre-en-cuenta abre justamente en Cuenta. El ícono
  // acompaña: el monigote se lee como "tu perfil" y repetía el del avatar de arriba.
  { label: 'Configuración', icono: 'settings', ruta: RUTA_PERFIL },
  // "Suscripción" navega a `/subscription`, la página real de planes
  // (ADR-2026-08-04-suscripcion-navega-a-subscription, supersede
  // ADR-2026-08-04-suscripcion-abre-el-catalogo-sin-escala). El overlay del catálogo
  // sigue siendo lo que abre "Mejorar plan" de la tarjeta de cuenta; el ítem del menú
  // ahora lleva a la vista completa.
  { label: 'Suscripción', icono: 'credit_card', ruta: '/subscription' },
  // "Invitá y ganá" (ADR-2026-08-27-programa-referidos): también en el menú de
  // cuenta y no sólo en el pie de la sidebar, porque en mobile la sidebar no está
  // y el menú del avatar es la única puerta a las secciones de la cuenta.
  { label: 'Invitá y ganá', icono: 'redeem', ruta: '/referidos' },
  { label: 'Centro de ayuda', icono: 'support_agent', ruta: '/ayuda' },
];

/**
 * Menú de cuenta que se abre desde el avatar — ADR-2026-07-31-menu-cuenta-avatar-uno-solo.
 *
 * Un solo componente para los dos avatares (el del saludo, arriba a la derecha, y el
 * de la tarjeta de cuenta, abajo en la sidebar): son el mismo menú, y duplicarlo
 * garantizaba que se desincronizaran.
 *
 * Se replica la ESTRUCTURA de la referencia —cabecera con nombre y mail, ítems con
 * ícono, divisor y cerrar sesión al final— no su paleta: la referencia es oscura
 * porque esa app lo es, y acá el shell es claro.
 *
 * `variante` decide el anclaje, que es lo único que cambia entre los dos:
 * - `home`: cuelga del avatar, alineado a la derecha. `absolute`, así acompaña al
 *   contenido si la página scrollea.
 * - `sidebar`: sale hacia la derecha del avatar y crece hacia arriba. Va `fixed` y
 *   con coordenadas medidas al abrir, porque el `<aside>` tiene `overflow-hidden`
 *   y un hijo absoluto quedaría cortado por el borde del panel.
 *
 * El menú se puede abrir desde AFUERA del avatar (`ref.alternar()`): en la sidebar
 * lo dispara la tarjeta de cuenta entera, que antes navegaba al perfil. El
 * disparador no puede envolver a la tarjeta —adentro vive "Mejorar plan" y anidar
 * botones es HTML inválido—, así que la tarjeta llama al handle. `zona` es esa
 * misma tarjeta: sin ella, el click de apertura cuenta como "click afuera", cierra
 * el menú en `mousedown` y lo vuelve a abrir en `click`, con lo que la tarjeta
 * nunca lo cerraba.
 */
type MenuCuentaHandle = { alternar: () => void };

/**
 * Posición del menú, medida al abrir. Los cuatro lados son opcionales porque cada
 * anclaje usa dos: la sidebar `left`+`bottom` (crece hacia arriba, a la derecha del
 * panel) y el encabezado `top`+`right` (cuelga del avatar).
 */
type Coords = { left?: number; right?: number; top?: number; bottom?: number };

// Exportado para la home, que vive en `HomeMinimal.tsx`
// (ADR-2026-09-01-la-version-2-es-la-home).
export const MenuCuenta = forwardRef<
  MenuCuentaHandle,
  {
    variante: 'home' | 'sidebar';
    children: ReactNode;
    /** Área que cuenta como "adentro" del menú además del propio menú. */
    zona?: RefObject<HTMLElement | null>;
  }
>(function MenuCuenta({ variante, children, zona }, ref) {
  const navigate = useNavigate();
  // Identidad y plan reales (ADR-2026-07-31-shell-nuevo-datos-reales). La `etiqueta` accesible se arma
  // acá y ya no la pasa cada llamador: los tres pasaban exactamente el mismo
  // string, y con el nombre viniendo de sesión eso obligaba a que los tres lo
  // leyeran del contexto sólo para reenviarlo.
  const { nombreCompleto, email, planLabel, esDueno } = useContext(CuentaContext);
  const etiqueta = nombreCompleto
    ? `${nombreCompleto} — ${planLabel}`
    : 'Abrir menú de cuenta';
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);
  // El menú va PORTALEADO a `document.body`, así que ya no es descendiente de
  // `contenedor` y hay que preguntarle por separado si el click cayó adentro.
  const menuEl = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        contenedor.current?.contains(t) ||
        menuEl.current?.contains(t) ||
        zona?.current?.contains(t)
      )
        return;
      setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto, zona]);

  const alternar = () => {
    if (!abierto && disparador.current) {
      const r = disparador.current.getBoundingClientRect();
      if (variante === 'sidebar') {
        // Se ancla al borde derecho del PANEL, no al del avatar: colgado del avatar,
        // con la sidebar expandida el menú caía encima de la lista de herramientas.
        const panel = disparador.current.closest('aside')?.getBoundingClientRect();
        // Crece hacia ARRIBA: en la sidebar el disparador está abajo de todo y un menú
        // que baje se saldría de la ventana.
        //
        // El piso es el de la TARJETA (`zona`), no el del avatar: el avatar vive en la
        // fila de arriba de la tarjeta, así que anclando ahí el menú terminaba flotando
        // a media altura, sin apoyarse en nada. Con el borde de abajo alineado, el menú
        // y la tarjeta cierran en la misma línea. Sin `zona` —la sidebar colapsada, que
        // no tiene tarjeta— sigue valiendo el pie del avatar.
        // Los 2px de más bajan el menú lo justo para que su sombra no se lea como si
        // arrancara arriba del borde de la tarjeta.
        const piso = (zona?.current ?? disparador.current).getBoundingClientRect().bottom;
        setCoords({
          left: (panel?.right ?? r.right) + 10,
          bottom: window.innerHeight - piso - 2,
        });
      } else {
        // `home`: cuelga del avatar, alineado a su borde derecho. Antes era un
        // `absolute` contra el contenedor; con el portal el contenedor ya no es el
        // ancestro posicionado, así que se mide igual que el de la sidebar.
        setCoords({ top: r.bottom + 8, right: window.innerWidth - r.right });
      }
    }
    setAbierto((v) => !v);
  };

  useImperativeHandle(ref, () => ({ alternar }));

  const cerrarYNavegar = (ruta: string) => {
    setAbierto(false);
    navigate(ruta);
  };

  const claseItem =
    'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-[13.5px] font-[500] text-black/75 transition-colors hover:bg-black/[0.04] hover:text-black';

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
        className="block rounded-xl"
      >
        {children}
      </button>

      {abierto && createPortal(
        <div
          ref={menuEl}
          role="menu"
          // El menú corta la propagación de sus propios clicks. En la sidebar vive
          // DENTRO de la tarjeta de cuenta, que es clickeable (abre este mismo menú):
          // sin esto, elegir un ítem navegaba y en el mismo gesto la tarjeta volvía a
          // alternar el menú. Un portal NO cambia esto —los eventos de React siguen
          // burbujeando por el árbol de componentes, no por el DOM—.
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ position: 'fixed', ...coords }}
          className="animate-fade-in z-[120] w-[268px] rounded-2xl border border-black/[0.08] bg-white p-1.5 shadow-[0_16px_44px_rgba(16,24,45,0.16)]"
        >
          {/* Cabecera: quién sos. Sin acción propia —en la referencia tampoco la
              tiene—; "Perfil" es el ítem que lleva a editarlo. */}
          <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-2.5">
            <AvatarUsuario px={40} />
            <div className="min-w-0 flex-1">
              {/* Sin nombre cargado, el mail sube a la línea principal en vez de
                  dejar un renglón vacío: es el identificador que el usuario sí
                  reconoce, y siempre existe con sesión. */}
              <p className="truncate text-[14px] font-semibold leading-tight">
                {nombreCompleto ?? email}
              </p>
              {nombreCompleto && email && (
                <p className="mt-0.5 truncate text-[12.5px] leading-tight text-black/45">
                  {email}
                </p>
              )}
            </div>
          </div>

          <div className="mt-1 flex flex-col gap-0.5">
            {/* "Suscripción" es del dueño: un miembro no cambia ni cancela el plan
                (spec 2026-09-11-invitaciones-equipo §4.3). */}
            {ITEMS_MENU_CUENTA.filter((item) => item.label !== 'Suscripción' || esDueno).map((item) =>
              item.url ? (
                <a
                  key={item.label}
                  role="menuitem"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAbierto(false)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  role="menuitem"
                  type="button"
                  onClick={() => cerrarYNavegar(item.ruta!)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </button>
              ),
            )}
          </div>

          <div className="my-1.5 h-px bg-black/[0.07]" />

          {/* Cierre de sesión REAL, el mismo que el Sidebar de producción
              (`lib/cerrarSesion`): es la única parte del menú que no puede ser
              decorativa sin volverse mentirosa. */}
          <button
            role="menuitem"
            type="button"
            onClick={async () => {
              setAbierto(false);
              await cerrarSesion();
            }}
            className={claseItem}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
              logout
            </span>
            Cerrar sesión
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
});

/**
 * Foto de perfil de una marca: el logo cargado si lo hay, y si no la inicial del
 * nombre en el chip azulado — ADR-2026-08-04-la-marca-se-ve-con-su-logo.
 *
 * La inicial es el RESPALDO y no la identidad: una marca con logo tiene que verse con
 * su logo en todos lados. El menú desplegable ya hacía esto; la tarjeta de la sidebar
 * —la que se ve siempre, incluso plegada— seguía en la inicial.
 *
 * Si la URL del logo no carga (borrado del bucket, firma vencida) vuelve a la inicial
 * en vez de dejar el ícono de imagen rota. `rota` guarda la URL que falló y no un
 * booleano, así el respaldo se levanta solo al cambiar de marca —sin efecto de reset—.
 *
 * La caja (lado y radio) la pone quien lo usa: 35px en la tarjeta para atarse al alto
 * del bloque de texto, 28 en el menú.
 */
export function AvatarMarca({
  logo,
  nombre,
  className,
  claseInicial,
}: {
  logo: string | null;
  nombre: string;
  className: string;
  claseInicial: string;
}) {
  const [rota, setRota] = useState<string | null>(null);

  if (logo && rota !== logo) {
    return (
      <img
        src={logo}
        alt=""
        onError={() => setRota(logo)}
        className={`${className} border border-black/[0.06] bg-white object-contain`}
      />
    );
  }
  return (
    <span
      className={`${className} grid place-items-center bg-[#EBEDF7] font-bold text-brand-primary ${claseInicial}`}
    >
      {nombre?.[0] ?? '?'}
    </span>
  );
}

/**
 * Menú de marcas que se abre desde la tarjeta de marca de la sidebar —
 * ADR-2026-08-03-tarjeta-marca-abre-menu-de-marcas.
 *
 * La tarjeta antes navegaba directo a `/mi-marca`. Ahora despliega: las marcas de la
 * cuenta (con la activa marcada, y cambiar es un click), "Editar marca actual" —el
 * destino viejo, que no se pierde— y "Añadir nueva marca".
 *
 * Es un menú y no un modal como el del Sidebar de producción: en este shell el bloque
 * de identidad de arriba a la derecha ya despliega un menú anclado a su disparador, y
 * la tarjeta de marca es el mismo tipo de control. Un modal centrado para elegir entre
 * dos ítems tapa la pantalla entera para nada.
 *
 * Va `fixed` con coordenadas medidas al abrir, por lo mismo que `MenuCuenta`: el
 * `<aside>` tiene `overflow-hidden` y un hijo absoluto quedaría cortado por el borde
 * del panel. Crece hacia ABAJO porque la tarjeta vive arriba de todo.
 */
function MenuMarcas({
  marcas,
  marcaActivaId,
  cambiarMarca,
  cambiando,
  onEditarMarca,
  children,
}: {
  marcas: BrandRow[];
  marcaActivaId: string | null;
  cambiarMarca: (id: string) => void;
  cambiando: boolean;
  /** Destino de "Editar marca actual" — lo pone el shell, con su fade. */
  onEditarMarca: () => void;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  // El plan se consulta al INTENTAR crear, no al abrir el menú: la gran mayoría de
  // las aperturas es para cambiar de marca, y una consulta por apertura sería
  // gratuita sólo en apariencia.
  const [pidiendoPlan, setPidiendoPlan] = useState(false);
  /**
   * Multi-marca es una función de plan pago. Cuando el plan no la incluye se abre el
   * CATÁLOGO —el mismo que abren los ítems con candado de la sidebar y las cards
   * bloqueadas de la tira— y no `SubscriptionLimitModal`
   * (ADR-2026-08-03-anadir-marca-bloqueada-abre-el-catalogo). Antes eran dos carteles
   * distintos para la misma situación: "esto viene con otro plan".
   */
  const { abrirPricing } = useContext(CuentaContext);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);
  // Portaleado a `document.body` (ver el comentario del `createPortal` abajo), así que
  // el menú ya no es descendiente de `contenedor` y se pregunta aparte.
  const menuEl = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (contenedor.current?.contains(t) || menuEl.current?.contains(t)) return;
      setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const alternar = () => {
    if (!abierto && disparador.current) {
      const r = disparador.current.getBoundingClientRect();
      // Se abre AL COSTADO, como el menú de perfil del pie
      // (ADR-2026-08-03-menu-marcas-al-costado): anclado al borde derecho del PANEL
      // —no al de la tarjeta— para que los dos menús de la sidebar salgan de la
      // misma línea vertical, con o sin la sidebar colapsada.
      //
      // Colgado hacia abajo, como estaba, el menú tapaba la lista de herramientas
      // entera: la tarjeta de marca vive arriba de todo y el menú tiene cinco ítems.
      //
      // La diferencia con el de perfil es hacia dónde crece. Aquél se ancla por el
      // piso porque su disparador está abajo de todo; éste se ancla por el techo,
      // alineado con el borde de arriba de la tarjeta, porque crece hacia abajo.
      const panel = disparador.current.closest('aside')?.getBoundingClientRect();
      setCoords({ left: (panel?.right ?? r.right) + 10, top: r.top });
    }
    setAbierto((v) => !v);
  };

  const elegir = (id: string) => {
    setAbierto(false);
    cambiarMarca(id);
  };

  /**
   * Multi-marca es una función de plan pago (`planAllowsMultiBrand`). El criterio es
   * el MISMO que el del Sidebar de producción —se consulta el plan y, si no alcanza,
   * no se crea— y no una regla nueva: dos criterios para la misma puerta se
   * desincronizan al primer cambio de planes.
   *
   * Lo que cambia acá es lo que se muestra cuando no alcanza: el catálogo de planes
   * en vez de `SubscriptionLimitModal`
   * (ADR-2026-08-03-anadir-marca-bloqueada-abre-el-catalogo).
   */
  const crearMarca = async () => {
    if (pidiendoPlan) return;
    setPidiendoPlan(true);
    try {
      const plan = await getUserPlan();
      setAbierto(false);
      if (!planAllowsMultiBrand(plan)) {
        // Mismo motivo que los otros accesos bloqueados: trae el copy "Desbloqueá
        // todo el potencial de Estudio Atlas", que no habla de créditos —acá el saldo
        // no tiene nada que ver, es el plan—.
        abrirPricing('bloqueado');
        return;
      }
      navigate('/redes-sociales/descripcion-brand?mode=additional');
    } finally {
      setPidiendoPlan(false);
    }
  };

  const claseItem =
    'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[13.5px] font-[500] text-black/75 transition-colors hover:bg-black/[0.04] hover:text-black disabled:opacity-50';

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="block w-full rounded-xl"
      >
        {children}
      </button>

      {abierto && coords && createPortal(
        <div
          ref={menuEl}
          role="menu"
          // Mismo corte que en `MenuCuenta`: el menú no deja que sus clicks lleguen a
          // lo que haya arriba, que en la sidebar es una tarjeta clickeable.
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          // El alto lo pone el CONTENIDO — ADR-2026-08-04-el-menu-de-marcas-crece-con-las-marcas.
          // El tope es lo que queda de ventana desde donde arranca el menú (menos
          // 12px de aire abajo): con pocas marcas no se nota, y con muchas el menú
          // crece hasta el borde en vez de recortarse a una altura fija.
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            maxHeight: `calc(100dvh - ${coords.top}px - 12px)`,
          }}
          className="animate-fade-in z-[120] flex w-[268px] flex-col rounded-2xl border border-black/[0.08] bg-white p-1.5 shadow-[0_16px_44px_rgba(16,24,45,0.16)]"
        >
          {/* Rótulo y no un ítem: la lista necesita decir de qué es, y con una sola
              marca el menú si no arrancaría directo en las acciones. */}
          <p className="px-2.5 pb-1.5 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
            Tus marcas
          </p>

          {/* Sin alto fijo: la lista se estira con las marcas que haya y sólo scrollea
              si el menú entero ya llegó al borde de la ventana (el `maxHeight` de
              arriba). `min-h-0` es lo que le permite encogerse dentro del flex —
              sin eso, un hijo flex no baja de su contenido y el scroll nunca aparece,
              con el menú desbordando la pantalla. */}
          <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
            {marcas.map((m) => {
              const activa = m.id === marcaActivaId;
              return (
                <button
                  key={m.id}
                  role="menuitem"
                  type="button"
                  onClick={() => elegir(m.id)}
                  disabled={cambiando}
                  className={claseItem}
                >
                  {/* El logo si lo hay, y si no la inicial en el mismo chip que usa la
                      tarjeta: así una marca sin logo no se ve como un hueco roto.
                      `rounded-sm` = 8px en este config (no el 2px de Tailwind por
                      defecto: la escala está pisada en `index.css`). Ojo con `lg`,
                      que acá son 16px y a 28px de lado recorta un círculo. */}
                  <AvatarMarca
                    logo={m.logo_url}
                    nombre={m.name}
                    className="h-7 w-7 shrink-0 rounded-sm"
                    claseInicial="text-[11px]"
                  />
                  {/* Nombre y rubro apilados, igual que en la tarjeta que abre este
                      menú: con varias marcas del mismo rubro —o con nombres cortos
                      como "Belle" / "Ropa"— el nombre solo no alcanzaba para saber
                      cuál es cuál. El rubro sale de `brand_profiles.rubro`; si la
                      marca no lo tiene, no se reserva la línea. */}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{m.name}</span>
                    {m.rubro && (
                      <span className="truncate text-[11.5px] font-[400] leading-[15px] text-black/45">
                        {m.rubro}
                      </span>
                    )}
                  </span>
                  {activa && (
                    <span className="material-symbols-rounded shrink-0 text-[18px] text-brand-primary">
                      check
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="my-1.5 h-px bg-black/[0.07]" />

          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setAbierto(false);
              onEditarMarca();
            }}
            className={claseItem}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
              tune
            </span>
            Editar marca actual
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={crearMarca}
            disabled={pidiendoPlan}
            className={`${claseItem} text-brand-primary hover:text-brand-primary`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">add</span>
            Añadir nueva marca
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Acá vivían `CardModo` y `CarruselModos`, la tira de modos que abría la home: todos
 * los modos visibles a la vez, el activo expandido por `flex-grow` y su carátula en
 * video corriendo una vez.
 *
 * La home dejó de tener tira en ADR-2026-09-01-la-version-2-es-la-home: arriba ahora van
 * dos cards de vertiente que abren un popup con los modos de su familia
 * (`HomeMinimal.tsx`). Sin ese llamador los dos componentes quedaron sin usar, así que
 * se borraron en vez de quedar como código muerto que describe una pantalla que ya no
 * existe. Están en el historial si alguna vez hay que volver a mirarlos.
 */

/**
 * Celda "Ver más": el acceso a la lista completa vive DENTRO de la grilla, como
 * sexta celda, a la misma altura que lo que muestra.
 *
 * Nació en las filas de Plantillas y desde ADR-2026-08-03-ver-mas-en-creaciones-recientes
 * la comparte también Creaciones recientes. Se extrajo en vez de copiarse: es la
 * misma pieza —mismo tamaño, mismo punteado, mismo hover azul— y duplicada se iban
 * a separar al primer retoque.
 *
 * `etiqueta` es el `aria-label`: el texto visible es siempre "Ver más", que sin
 * contexto no dice de qué, y cada sección sabe a dónde lleva la suya.
 *
 * `fondo` es lo único que cambia entre las dos, porque cada una se apoya sobre una
 * superficie distinta: en Plantillas la celda vive DENTRO de la tarjeta blanca de
 * la colección, así que va un tono por debajo (`vacio`) para no desaparecer contra
 * ella; en Creaciones recientes la grilla está sobre el lienzo gris pelado, y ahí
 * el blanco es lo que la empareja con las miniaturas de al lado
 * (ADR-2026-08-03-ver-mas-en-creaciones-recientes).
 */
function CeldaVerMas({
  etiqueta,
  onClick,
  href,
  tema,
  fondo,
  medidas,
  estilo,
}: {
  etiqueta: string;
  onClick: () => void;
  /** Destino, si la celda navega: la vuelve un enlace y habilita el Cmd/Ctrl+clic. */
  href?: string;
  tema: TemaMinimal;
  /** Clase de fondo. Por defecto el de Plantillas, que fue el caso original. */
  fondo?: string;
  /**
   * Cómo se dimensiona la celda. El defecto es el de Plantillas —una columna más de
   * la grilla, con la proporción 3:4 de sus miniaturas—; Creaciones lo pisa porque su
   * fila es de alto fijo y ancho variable
   * (ADR-2026-08-03-creaciones-recientes-respetan-su-forma).
   */
  medidas?: string;
  /** Va con `medidas` cuando la proporción no se puede escribir como clase. */
  estilo?: React.CSSProperties;
}) {
  return (
    <EnlaceApp
      href={href}
      onNavegar={onClick}
      aria-label={etiqueta}
      style={estilo}
      className={`group flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.14] transition-colors hover:border-brand-primary/40 hover:bg-[#F3F5FD] ${medidas ?? `aspect-[3/4] ${CELDA_MINIATURA}`} ${fondo ?? tema.vacio}`}
    >
      <span
        className={`grid h-9 w-9 place-items-center rounded-full text-black/55 shadow-sm transition-colors group-hover:text-brand-primary ${tema.hueco}`}
      >
        <span className="material-symbols-rounded text-[20px]">arrow_forward</span>
      </span>
      <span className="text-[12.5px] font-medium text-black/60 transition-colors group-hover:text-brand-primary">
        Ver más
      </span>
    </EnlaceApp>
  );
}

/**
 * Plan del usuario para gatear secciones de la home —
 * ADR-2026-08-18-creaciones-recientes-solo-para-planes-pagos.
 *
 * Mismo arranque que `/modos`, el sheet de generar y el drawer de Mi marca: el cache
 * que llena el Sidebar (`atlas-user-plan`) para decidir ya en el primer paint, y el
 * valor real de Supabase después. No se lee de `CuentaContext` a propósito: ese
 * contexto expone `planLabel` (una etiqueta para mostrar, no el plan) y encima está
 * en su default inerte en mobile, donde `InterfazV2` monta las secciones FUERA del
 * shell — o sea que ahí no diría nada.
 *
 * `null` = todavía no se sabe. Para lo que se gatea con esto, desconocido cuenta
 * como gratis: es preferible que a un pago le aparezca la sección un instante
 * después a que a un gratis le parpadee una sección que no le corresponde.
 */
function usePlanUsuario(): string | null {
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);
  return plan;
}

/**
 * ¿Se muestra "Creaciones recientes"? Envoltorio del predicado puro
 * `muestraCreacionesRecientes` (`lib/seccionesHome.ts`), donde están la regla y el
 * porqué de cada default — ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda.
 *
 * Existe como HOOK COMPARTIDO, y no como un `if` adentro de la sección, porque lo
 * consumen DOS componentes que tienen que decir lo mismo: la sección
 * (`CreacionesRecientes`) y el ESQUELETO de la home (`HomeMinimalSkeleton`), que reserva
 * —o no— la fila. Si discrepan, el resultado es justo el parpadeo que hay que evitar: el
 * esqueleto promete una fila que después no aparece y la home entera sube de golpe.
 */
function useMuestraCreacionesRecientes(): boolean {
  const plan = usePlanUsuario();
  const { rubro, loaded: rubroResuelto } = useRubroMarcaActiva();
  return muestraCreacionesRecientes(plan, rubro, rubroResuelto);
}

/**
 * "Creaciones recientes" con las generaciones REALES del usuario —
 * ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * Reemplaza a `GrillaImagenes`, el bloque genérico "título + bajada + grilla" que
 * compartía con Plantillas. Se borró en vez de extenderlo: las tres cosas que esta
 * sección necesita —cada celda abre SU imagen y no la sección, un estado de carga
 * que no puede parpadear y un estado vacío— no existen en una grilla de catálogo, y
 * Plantillas hacía rato que renderizaba su propia grilla (con la celda "Ver más").
 * El "genérico" tenía un solo consumidor.
 *
 * Abrir una creación ya NO navega al historial: monta ese mismo componente en modo
 * "sólo visor" y la muestra ampliada acá (ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home).
 * Antes se navegaba con `state.expandImageId`, que era irse de la home para ver una
 * imagen. "Ver todo" y "Ver más" sí siguen yendo al historial: eso es una lista.
 */
function CreacionesRecientes({
  idCompany,
  idMarca,
  tema,
}: {
  idCompany: string | null;
  /** Marca activa: la sección muestra sólo lo generado para ELLA
   *  (ADR-2026-08-03-creaciones-recientes-por-marca). */
  idMarca: string | null;
  tema: TemaMinimal;
}) {
  const navigate = useNavigate();
  /**
   * Quién ve esta sección — ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda.
   * Era "sólo planes pagos"; ahora se esconde sólo en la prueba gratis DE MODA.
   *
   * El gate vive acá adentro y no en `SeccionesHome` porque así vale para las DOS
   * homes sin enhebrar una prop: desktop la monta desde `HomeMinimal` y mobile desde
   * `InterfazV2`, y las dos pasan por este componente. El esqueleto usa el MISMO hook,
   * que es lo que evita que prometa una fila que después no aparece.
   */
  const seVe = useMuestraCreacionesRecientes();
  // Si no se ve no se consulta nada: `null` como empresa es lo que el hook ya
  // entiende como "no hay a quién pedirle creaciones", así que la home de quien no
  // la ve no dispara ni la query a `imagenes_generadas` ni las firmas de Storage.
  const { items, cargando, hayMas } = useCreacionesRecientes(
    seVe ? idCompany : null,
    6,
    idMarca,
  );
  /**
   * Proporciones medidas sobre la miniatura ya cargada —
   * ADR-2026-08-03-creaciones-recientes-respetan-su-forma. Se llenan en el `onLoad`.
   *
   * Entran acá dos casos, y por eso lo medido le GANA a lo declarado:
   * - las creaciones sin `formato` en la base (una de cada cuatro);
   * - **todos los videos**, porque su formato declarado no es de fiar
   *   (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar).
   */
  const [ratiosMedidos, setRatiosMedidos] = useState<Record<string, number>>({});

  /**
   * Con qué proporción se dibuja: lo MEDIDO manda, lo declarado es la apuesta
   * inicial y 4:5 el respaldo mientras no haya ni una cosa ni la otra.
   */
  // `useCallback` y no `React.useCallback`: este módulo importa los hooks por nombre
  // y NO tiene `import React` (ver la nota del `useMemo` de abajo).
  const ratioDe = useCallback(
    (it: CreacionReciente) =>
      ratiosMedidos[it.id] ?? it.ratio ?? RATIO_CREACION_DEFECTO,
    [ratiosMedidos],
  );

  /**
   * Cuántas miniaturas entran, medido contra el ancho REAL de la fila —
   * ADR-2026-08-03-creaciones-entran-las-que-entran.
   *
   * Se mide el contenedor con un `ResizeObserver` y no se calcula desde el viewport:
   * el ancho depende de la columna del shell, de si la sidebar está colapsada y del
   * zoom, y cualquiera de esas cambia sin que cambie el viewport.
   *
   * `anchoFila = 0` significa "todavía no se midió" (primer render): ahí no se corta
   * nada, así el primer pintado no muestra una sola miniatura y después mete el resto.
   */
  const filaRef = useRef<HTMLDivElement | null>(null);
  const [anchoFila, setAnchoFila] = useState(0);
  useEffect(() => {
    const el = filaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(([entrada]) => {
      setAnchoFila(entrada.contentRect.width);
    });
    observador.observe(el);
    return () => observador.disconnect();
  }, [cargando, items.length]);

  /**
   * En mobile la fila scrollea y se muestran todas: ahí no entra ni una miniatura al
   * lado de "Ver más", y cortar la lista dejaría la sección con una sola imagen.
   */
  const [esAngosta, setEsAngosta] = useState(false);
  useEffect(() => {
    const consulta = window.matchMedia('(max-width: 639px)');
    const leer = () => setEsAngosta(consulta.matches);
    leer();
    consulta.addEventListener('change', leer);
    return () => consulta.removeEventListener('change', leer);
  }, []);

  /**
   * Qué miniaturas se dibujan y si hace falta "Ver más" —
   * ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas.
   *
   * "Ver más" aparece sólo cuando queda algo afuera, y hay dos formas de que quede
   * algo afuera: que el hook haya encontrado más de las que trae (`hayMas`), o que no
   * entren todas en el ancho disponible.
   *
   * El orden de las preguntas importa: primero se calcula sin reservarle lugar a la
   * celda, porque si entran todas así, esa celda no va — y reservarle lugar de entrada
   * habría dejado una miniatura afuera creando el motivo para mostrarla.
   */
  // `useMemo` y no `React.useMemo`: este módulo importa los hooks por nombre y NO
  // tiene `import React`, así que `React` no existe en runtime — era un
  // `ReferenceError` al renderizar las creaciones recientes, o sea la home entera.
  const { visibles, mostrarVerMas } = useMemo(() => {
    if (esAngosta || !anchoFila) {
      // Mobile (y el primer render, antes de medir): la fila scrollea y se muestran
      // todas, así que sólo puede quedar afuera lo que el hook no trajo.
      return { visibles: items, mostrarVerMas: hayMas };
    }
    const ratios = items.map(ratioDe);
    const sinReserva = cuantasCreacionesEntran(anchoFila, ratios, 0);
    if (sinReserva >= items.length && !hayMas) {
      return { visibles: items, mostrarVerMas: false };
    }
    const conReserva = cuantasCreacionesEntran(anchoFila, ratios, MIN_VER_MAS_PX);
    return { visibles: items.slice(0, conReserva), mostrarVerMas: true };
  }, [items, hayMas, ratioDe, anchoFila, esAngosta]);

  /**
   * Creación abierta en el visor — ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home.
   * Tocar una miniatura ya no navega al historial: monta ese mismo componente en modo
   * "sólo visor", que dibuja la imagen ampliada con todas sus acciones y nada más.
   */
  const [creacionAbierta, setCreacionAbierta] = useState<string | null>(null);
  /**
   * Las dos puntas de la transición del velo —
   * ADR-2026-08-03-el-visor-de-la-home-abre-con-transicion.
   *
   * `veloEncendido` lo prende un frame DESPUÉS de montar: si montara ya en
   * `opacity-100` no habría de dónde transicionar y el negro entraría de golpe.
   * `visorAbierto` lo apaga cuando el visor arranca su propia apertura, así los dos
   * se cruzan en vez de que uno reemplace al otro.
   */
  const [veloEncendido, setVeloEncendido] = useState(false);
  const [visorAbierto, setVisorAbierto] = useState(false);
  useEffect(() => {
    if (!creacionAbierta) {
      setVeloEncendido(false);
      setVisorAbierto(false);
      return;
    }
    // Doble rAF —el mismo patrón que usa `openLightbox` en el historial—: uno solo no
    // garantiza que el navegador haya pintado el estado inicial.
    let segundo = 0;
    const primero = requestAnimationFrame(() => {
      segundo = requestAnimationFrame(() => setVeloEncendido(true));
    });
    return () => {
      cancelAnimationFrame(primero);
      cancelAnimationFrame(segundo);
    };
  }, [creacionAbierta]);

  /** "Ver todo" y "Ver más": estos SÍ van al historial, que es una lista. */
  const irAlHistorial = () => navigate(RUTA_HISTORIAL);

  // Recién acá el corte, y no arriba: todos los hooks de este componente tienen que
  // haberse llamado igual en los dos casos.
  if (!seVe) return null;

  return (
    <section className="mt-12">
      {/* "Ver todo" PEGADO al título (12px), no empujado al borde derecho: sin la
          bajada la fila quedaba con el botón solo a media pantalla de distancia.
          `items-end` lo apoya en el pie del título en vez de centrarlo contra sus
          22px, que lo dejaban flotando a media altura. */}
      <div className="flex items-end gap-3">
        <div>
          <h2 className="text-[22px] font-bold tracking-[-0.01em]">Creaciones recientes</h2>
        </div>
        {/* "Ver todo" sólo con algo que ver: sobre el estado vacío mandaría al
            historial vacío, y la tarjeta de abajo ya ofrece la acción que sí sirve. */}
        {(cargando || items.length > 0) && (
          <EnlaceApp
            href={RUTA_HISTORIAL}
            onNavegar={irAlHistorial}
            className={BOTON_VER_TODO}
          >
            Ver todo <MoveRight className="h-4 w-4" strokeWidth={2.25} />
          </EnlaceApp>
        )}
      </div>

      {cargando ? (
        // Seis celdas del MISMO tamaño que las reales: la sección no cambia de alto
        // al llegar los datos, así que lo de abajo (Plantillas, Ediciones rápidas)
        // no salta.
        <div className={`mt-5 ${FILA_CREACIONES}`}>
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              style={{ aspectRatio: RATIO_CREACION_DEFECTO }}
              className={`shrink-0 animate-pulse rounded-xl border border-black/[0.06] ${ALTO_CREACION} ${tema.hueco}`}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        // Vacío REAL: una cuenta nueva no generó nada todavía. Antes esto no podía
        // pasar porque las imágenes eran de demo, y la home le decía "lo último que
        // generaste" a alguien que no había generado nada.
        // `bg-white` y no `tema.vacio` (ADR-2026-08-04-el-estado-vacio-de-creaciones-en-una-linea):
        // el gris del token es casi el del lienzo y la caja se leía como un hueco.
        // Va literal acá y no en el tema porque `tema.vacio` lo comparte la celda
        // "Ver más" de Plantillas, que se pidió expresamente que NO fuera blanca
        // (ADR-2026-08-03-solo-el-ver-mas-de-creaciones-va-en-blanco).
        <div className="mt-5 flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/[0.14] bg-white px-6 py-12 text-center">
          <span
            className={`grid h-11 w-11 place-items-center rounded-full text-black/45 ${tema.hueco}`}
          >
            <span className="material-symbols-rounded text-[22px]">add_photo_alternate</span>
          </span>
          <p className="mt-3 text-[14.5px] font-semibold">Todavía no generaste nada</p>
          {/* Sin `max-w-sm`: la frase mide 387px en una línea y ese tope son 384 —
              se partía en dos por TRES píxeles
              (ADR-2026-08-04-el-estado-vacio-de-creaciones-en-una-linea). Sin tope,
              usa el ancho que haya: una línea donde entra y dos en un teléfono, que
              es donde el corte tiene sentido. */}
          <p className="mt-1 text-[13px] leading-relaxed text-black/45">
            Elegí un modo de arriba y tu primera creación va a aparecer acá.
          </p>
        </div>
      ) : (
        // 5 creaciones + "Ver más" como sexta celda, igual que las filas de
        // Plantillas (ADR-2026-08-03-ver-mas-en-creaciones-recientes). El hook trae
        // 6 justamente para poder distinguir "hay más" de "esto es todo": si
        // volvieron 6, la sexta no se muestra y su lugar lo ocupa el acceso al
        // historial; con 5 o menos no hay resto que prometer y la celda no aparece.
        <div ref={filaRef} className={`mt-5 ${FILA_CREACIONES}`}>
          {visibles.map((it) => (
            <button
              key={it.id}
              onClick={() => setCreacionAbierta(it.id)}
              // Alto fijo + `aspectRatio` = el ancho lo pone la forma de la imagen.
              // Manda lo medido sobre la miniatura ya cargada; `formato` es la apuesta
              // inicial y 4:5 el respaldo, para que la fila no se reacomode a lo loco
              // mientras cargan.
              style={{ aspectRatio: ratioDe(it) }}
              className={`group relative shrink-0 overflow-hidden rounded-xl border border-black/[0.06] ${ALTO_CREACION} ${tema.tarjeta}`}
            >
              <img
                src={it.src}
                alt={it.alt}
                loading="lazy"
                // Se mide cuando lo declarado no alcanza o no es de fiar: las filas sin
                // `formato` (una de cada cuatro) y TODOS los videos, cuyo formato
                // declarado es el que pidió el flujo y no el del archivo
                // (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar). Para una
                // imagen con `formato` no se mide nada: la caja ya nace con la forma
                // correcta y no hay que esperar la descarga.
                onLoad={
                  it.ratio && !it.esVideo
                    ? undefined
                    : (e) => {
                        const img = e.currentTarget;
                        if (!img.naturalWidth || !img.naturalHeight) return;
                        const medido = img.naturalWidth / img.naturalHeight;
                        setRatiosMedidos((prev) =>
                          prev[it.id] === medido ? prev : { ...prev, [it.id]: medido },
                        );
                      }
                }
                // `object-cover` aunque la caja ya tenga la proporción: `formato` es lo
                // que se PIDIÓ al generar y puede no coincidir al pixel con lo que
                // devolvió el modelo. Sin él, esa diferencia se vería como una franja.
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              {/* El play va CENTRADO sobre la miniatura y más grande que el 24px que
                  tenía arriba a la izquierda: ahí competía con el contenido de la
                  esquina y a 14px casi no se leía. Centrado es donde se espera el
                  control de un video, y es el único lugar que no depende de qué haya
                  en la imagen. El contenedor se estira a toda la celda y centra con
                  `place-items-center` — así no hace falta un `translate`, que el
                  `:active { scale(.95) }` global de la app pisaría. */}
              {it.esVideo && (
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-black/50 text-white">
                    <span className="material-symbols-rounded text-[24px]">play_arrow</span>
                  </span>
                </span>
              )}
            </button>
          ))}

          {/* "Ver más" sólo cuando queda algo afuera
              (ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas). Si se ven todas, la fila
              termina donde termina la última miniatura: la celda no tendría nada que
              prometer y estaría ahí sólo para rellenar.

              Cuando sí va, es elástica: se estira para ocupar lo que sobre, así la
              fila cierra justo en el borde del contenedor sea cual sea la mezcla de
              formatos (ADR-2026-08-03-creaciones-entran-las-que-entran). */}
          {mostrarVerMas && (
          <CeldaVerMas
            etiqueta="Ver todas tus creaciones"
            onClick={irAlHistorial}
            tema={tema}
            // Blanco: acá la grilla va sobre el lienzo gris, no dentro de una
            // tarjeta, así que la celda tiene que apoyarse en la misma superficie
            // que las miniaturas que la acompañan.
            fondo={tema.tarjeta}
            // En mobile la fila scrollea, así que ahí conserva un ancho propio (la
            // proporción por defecto) en vez de estirarse: en un contenedor que
            // scrollea, `flex-1` no tiene sobrante que ocupar.
            medidas={`shrink-0 ${ALTO_CREACION} sm:min-w-[132px] sm:flex-1 sm:shrink`}
            estilo={{ aspectRatio: esAngosta ? RATIO_CREACION_DEFECTO : undefined }}
          />
          )}
        </div>
      )}

      {/* Visor de una creación, sin salir de la home
          (ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home). Se monta el historial
          en modo "sólo visor": trae el MISMO visor que esa pantalla, con todas sus
          acciones, y ninguna de su grilla.

          El velo de abajo es el que se ve mientras el historial resuelve su fetch: el
          visor recién puede abrirse cuando la imagen está en su lista. Es clickeable a
          propósito — si esa creación quedara fuera de sus filtros (fuera de los últimos
          30 días, por ejemplo) el visor no abriría nunca, y sin salida el usuario
          quedaría trabado. */}
      {creacionAbierta && (
        <>
          {/* Portaleado a `document.body` — ADR-2026-08-03-el-velo-del-visor-se-portalea.
              Inline, este `fixed` NO se resolvía contra la ventana: cuelga de
              `.aparece-tras-esqueleto`, que se anima con `animation-fill-mode: both` y
              queda con un `transform` de identidad puesto para siempre. Un transform
              —aunque no mueva nada— convierte al elemento en el bloque contenedor de
              sus descendientes `fixed`, así que el velo tomaba el alto y el ancho de la
              home: no tapaba la sidebar y el spinner quedaba centrado en el largo de la
              página, fuera de la vista. El visor de abajo no tiene el problema porque
              ya se portalea. */}
          {createPortal(
            <div
              role="presentation"
              onClick={() => setCreacionAbierta(null)}
              // 300ms contra los 200 del visor: el velo se apaga un poco más lento que
              // lo que tarda el visor en llegar a opaco, así en el cruce nunca queda un
              // hueco por el que se vea la home iluminada.
              // `pointer-events-none` al apagarse: apagado sigue estando ahí, y sin
              // esto se comería el click con el que se cierra el visor.
              className={`fixed inset-0 z-40 grid place-items-center bg-black/70 transition-opacity duration-300 ease-out ${
                visorAbierto
                  ? 'pointer-events-none opacity-0'
                  : veloEncendido
                    ? 'opacity-100'
                    : 'opacity-0'
              }`}
            >
              {/* El spinner entra recién a los 260ms: con una creación que abre rápido
                  —lo normal, porque la miniatura ya está en la cache de la home— un
                  spinner que parpadea 100ms es ruido, no información. */}
              <span style={{ animation: 'page-fade-in 200ms ease-out 260ms both' }}>
                <span className="block h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white" />
              </span>
            </div>,
            document.body,
          )}
          <GeneratedImagesHistory
            soloVisor={{
              imageId: creacionAbierta,
              onCerrar: () => setCreacionAbierta(null),
              onAbierto: () => setVisorAbierto(true),
            }}
          />
        </>
      )}
    </section>
  );
}

/**
 * Con `children` la pantalla deja de ser sólo la home y pasa a hacer de **shell**:
 * conserva sidebar, barra de créditos y panel dev, y en el área de contenido monta
 * lo que le pasen (`/creaciones`, `/editar-marca`). Ver ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo.
 *
 * No se extrajo el shell a un componente aparte a propósito: mover 180 líneas de
 * JSX de un árbol todavía sin commitear (ADR-2026-07-30-queda-solo-fuera-switcher-otras) es riesgo sin ganancia. Cuando
 * se commitee, el corte natural es `ShellHome` + `Home`.
 */
/**
 * El chat como una columna más del shell —
 * ADR-2026-08-20-el-chat-viaja-con-las-pantallas-que-lo-abren.
 *
 * Es un componente aparte y no unas líneas dentro de `EstiloMinimal` por una razón
 * concreta: `useEmpujeChat` lee `sidebarWidth` del `SidebarContext` para calcular el
 * ancho de la franja, y el que vale acá es el del shell (`ctxShell`), no el del
 * provider global. Un hook llamado en el cuerpo de `EstiloMinimal` no puede leer un
 * provider que el propio `EstiloMinimal` renderiza; montado adentro, sí. Así el ancho
 * de la franja y el hueco que le deja el contenido salen del MISMO número, que es la
 * invariante que `rutaChatAtlas.ts` pide en mayúsculas.
 *
 * El hueco es un hermano flex de `<main>` y no un `padding-right`: `<main>` es
 * `flex-1`, así que un espaciador al final de la fila le saca exactamente ese ancho.
 * El panel se dibuja `fixed right-0` con el mismo número, o sea justo encima del
 * espaciador.
 */
function ColumnaChatShell() {
  const { ancho } = useEmpujeChat();
  return (
    <>
      <div
        aria-hidden="true"
        className="shrink-0 transition-[width] duration-300 ease-out"
        style={{ width: ancho }}
      />
      <PanelChatAtlas />
      {/* El flotante que abre ese panel desde cualquier pantalla. Hermano del panel,
          por lo mismo que el panel: desde el cascarón, `fixed` es `fixed` de verdad
          (ADR-2026-09-10-el-boton-flotante-de-preguntale-a-atlas). */}
      <BotonFlotanteChat />
    </>
  );
}

export default function EstiloMinimal({
  variante = 'claro',
}: { variante?: VarianteMinimal } = {}) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  /**
   * ¿Esta ruta se hospeda en NEUTRO? —
   * ADR-2026-08-20-el-shell-hospeda-en-neutro-lo-que-no-nacio-para-la-columna.
   *
   * `true` para las que se mudaron acá desde `DashboardLayout` por el desktop: se
   * escribieron contra el panel de `.main-content` y hay que dejarlas con su ancho,
   * su padding y sus títulos. Ver el `<Outlet />` al final del archivo.
   */
  const hospedajeNeutro = esRutaShellLegacy(pathname);

  /** Si esta ruta dibuja la columna del chat. NO es lo mismo que el hospedaje. */

  // Fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El contenido se apaga, recién ahí se navega,
  // y al montar la sección nueva se vuelve a encender. La sidebar y la barra de
  // créditos quedan fuera de este div, así que ni parpadean.
  const [visible, setVisible] = useState(true);
  // Depende del `search` además del `pathname` — ADR-2026-08-31-el-fade-del-shell-mira-el-query.
  // Hay navegación de sidebar que cambia SÓLO el query (`/modos?familia=video` →
  // `?familia=imagen`, los dos "Ver más"): con el efecto atado al pathname, el fade
  // apagaba el contenido, navegaba, y nada lo volvía a prender — la sección quedaba
  // en blanco hasta cambiar de pantalla.
  useEffect(() => {
    setVisible(true);
  }, [pathname, search]);
  /**
   * El fade de salida, sin destino: apaga el contenido y corre `accion` cuando la
   * animación terminó. Es la mitad reutilizable de `irA` — la tira de modos de la
   * home necesita esta transición pero NO puede delegar la navegación, porque su
   * destino lo arma `InterfazV2` (ver `conFadeDeSalida` en `CuentaCtx`).
   */
  const conFadeDeSalida = useCallback((accion: () => void) => {
    setVisible(false);
    window.setTimeout(accion, FADE_SALIDA_MS);
  }, []);
  /* Los modos que este usuario abrió últimamente: el registro y la lista que
     alimenta los dos grupos de la sidebar —
     ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados.

     El registro va acá, en el shell, y no en cada modo: todas las pantallas de
     herramienta se montan adentro de este layout, así que este `pathname` las ve a
     todas —vengan de la sidebar, de `/modos`, de la home o de un link directo. */
  useRegistrarModoDeRuta(pathname);

  /** Navegación desde la sidebar: apaga, navega, y el efecto de arriba prende. */
  const irA = (ruta: string) => {
    if (ruta === pathname) {
      // Ya está en esa ruta, pero "Inicio" igual tiene trabajo: una plantilla se abre SOBRE
      // la home sin cambiar de ruta, y tocar Inicio no hacía nada. Se avisa por el estado
      // de la navegación y la home cierra lo que tenga abierto
      // (ADR-2026-10-04-inicio-cierra-la-plantilla-abierta). Sin fade: no cambia de pantalla.
      if (ruta === '/interfaz-v2') navigate(ruta, { replace: true, state: { irAlInicio: true } });
      return;
    }
    conFadeDeSalida(() => navigate(ruta));
  };
  /**
   * Abajo de 1024px la sidebar va SIEMPRE colapsada —
   * ADR-2026-08-05-el-shell-nuevo-arranca-en-768.
   *
   * Desplegada mide 260px con el margen. En un ancho de 800 eso deja 540 para el
   * contenido, y las secciones —pensadas para 1024 en adelante— entran a los golpes: el
   * saludo de la home se parte en tres líneas y la tira de modos se sale. Colapsada mide
   * 82 y quedan 718, que es donde el contenido vuelve a respirar.
   *
   * El toggle se esconde en ese rango en vez de quedar inerte: ofrecer un control que
   * no puede hacer nada es peor que no ofrecerlo. La preferencia guardada no se pisa —
   * se ignora mientras la ventana es angosta y vuelve sola al agrandarla.
   */
  const anchoDeSobra = useIsDesktop();
  const { colapsada: colapsadaPreferida, alternar, colapsar } = useSidebarColapsable();

  /*
   * ENTRAR A UN MODO YA NO COLAPSA LA SIDEBAR —
   * ADR-2026-09-04-entrar-a-un-modo-no-colapsa-la-sidebar.
   *
   * Acá vivía un estado paralelo (`colapsadaEnModo`) que arrancaba en `true` y se
   * forzaba a `true` en cada entrada a una pantalla de modo, con el toggle moviéndolo
   * a él en vez de a la preferencia. Ganaba 178px para la pantalla de trabajo, pero el
   * efecto se disparaba en CADA cambio de modo: quien abría la sidebar adentro de un
   * modo y pasaba al siguiente se la encontraba cerrada otra vez, sin haberla tocado.
   *
   * Hoy hay un solo estado —la preferencia guardada—, así que la sidebar queda como
   * esté en ese momento y el toggle significa lo mismo en toda la app. El único
   * override que sobrevive es el de ancho (`anchoDeSobra`), que es de layout y no de
   * preferencia.
   */
  const colapsada = !anchoDeSobra || colapsadaPreferida;
  const alternarSidebar = alternar;
  const tema = TEMAS[variante];

  /**
   * Etiquetas de los ítems mientras la sidebar está colapsada — reemplazan al `title`
   * nativo que tenían (ver `TooltipSidebar`). Sólo cuando está colapsada: desplegada,
   * cada ítem ya muestra su nombre al lado del ícono y un tooltip sería repetir lo
   * que se está leyendo.
   */
  const { propsTooltip, tooltip: tooltipSidebar } = useTooltipsSidebar(colapsada);

  // La tarjeta de cuenta del pie abre el menú del avatar en vez de navegar al
  // perfil (supersede ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil): es el
  // mismo bloque de identidad que arriba a la derecha, y ahí el click despliega el
  // menú. Llevar a una ruta desde un lado y desplegar del otro hacía que el mismo
  // gesto sobre el mismo dato hiciera dos cosas distintas. "Perfil" sigue estando,
  // ahora como ítem del menú.
  const menuCuentaSidebar = useRef<MenuCuentaHandle>(null);
  const tarjetaCuenta = useRef<HTMLDivElement>(null);

  // Contexto de sidebar RE-PROVISTO para las pantallas hospedadas (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell). Se
  // hereda el del provider real —así los setters del header mobile siguen llegando
  // a quien corresponde— y sólo se pisa la geometría, que es lo único que este
  // shell tiene distinto: su sidebar está en flujo, mide otra cosa y colapsa con su
  // propio estado. Sin esto, un panel `fixed` anclado a `sidebarWidth` se posiciona
  // contra una sidebar que en esta pantalla no existe.
  const ctxHeredado = useSidebar();
  // `hideSidebar` lo prende la pantalla hospedada que quiere el ancho entero (el
  // Estudio de Video). Viaja por el contexto heredado; acá se traduce a las dos
  // cosas que este shell controla: no dibujar el `<aside>` y publicar ancho 0,
  // que es contra lo que se posicionan los paneles `fixed` de las pantallas.
  const sidebarEscondida = ctxHeredado.hideSidebar;
  /**
   * LA TIRA (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira). Mientras una
   * pantalla tiene prendida `sidebarMinima` (el Agente, con o sin lienzo), la sidebar
   * COLAPSADA se dibuja como una tira con sólo el logo. Apagada, "colapsada" vuelve a
   * ser la de íconos de toda la app. Sólo en escritorio: abajo de 1024 la sidebar ya va
   * colapsada por ancho y sin íconos no quedaría navegación.
   *
   * Colapsarla es aparte, y de una vez (`pedidoDeColapsar`, cuando el lienzo aparece):
   * si el usuario la abre después, queda abierta hasta que la cierre, y cerrarla vuelve
   * a la tira.
   */
  const { sidebarMinima, pedidoDeColapsar } = ctxHeredado;
  useEffect(() => {
    if (pedidoDeColapsar > 0) colapsar();
  }, [pedidoDeColapsar, colapsar]);
  const minima = anchoDeSobra && colapsada && sidebarMinima;
  const ctxShell = useMemo(
    () => ({
      ...ctxHeredado,
      isCollapsed: colapsada,
      // Escondida (el Estudio de Video) o mínima (el lienzo del Agente): en las
      // dos la sidebar no ocupa lugar y lo que se publica es sólo el MARGEN, que
      // es contra lo que se apoyan los marcos `fixed` de las pantallas
      // hospedadas — con 0 se pegan al borde de la ventana
      // (ADR-2026-09-14-la-sidebar-escondida-mide-8-y-no-0).
      sidebarWidth:
        sidebarEscondida || minima
          ? MARGEN_SIDEBAR
          : (colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR) +
            MARGEN_SIDEBAR * 2,
      sidebarMinima: minima,
    }),
    [ctxHeredado, colapsada, sidebarEscondida, minima],
  );
  // Descarte sólo en memoria, a propósito: la barra avisa un problema real (saldo
  // en cero o por debajo del umbral) que no se resuelve cerrándola. Persistirlo
  // dejaría al usuario sin el aviso justo cuando le empieza a importar; se va por
  // lo que dura la sesión y vuelve al recargar.
  const [barraDescartada, setBarraDescartada] = useState(false);

  /**
   * Alto ocupado por la barra de créditos, publicado como variable CSS
   * (`--shell-barra-creditos`) sobre la raíz del shell —
   * ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra.
   *
   * Las pantallas de modo se montan como un panel `fixed inset-y-0`, así que TAPABAN
   * la barra: el aviso de saldo desaparecía apenas se entraba a un modo, que es
   * justo cuando importa. En vez de montar una segunda barra adentro del panel
   * —dos instancias, dos estados de descarte—, el panel arranca DEBAJO de ésta.
   *
   * Se mide el `offsetHeight` de la franja y NADA más. Antes era
   * `offsetTop + offsetHeight`, que con la barra en el flujo daba lo mismo —los 10px
   * de `offsetTop` eran el aire de arriba— pero desde que la franja es `sticky`
   * (ADR-2026-08-04-la-barra-de-creditos-no-scrollea) `offsetTop` crece con el scroll:
   * la caja pegada se re-posiciona, y los paneles se habrían ido bajando solos al
   * scrollear. El aire de arriba pasó a ser padding DE la franja, así que su alto ya
   * incluye todo lo que ocupa; y como `<main>` arranca en el tope de la ventana, ese
   * alto ES la coordenada donde termina la barra.
   */
  const barraCreditos = useRef<HTMLDivElement>(null);
  const [altoBarraCreditos, setAltoBarraCreditos] = useState(0);

  // Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). El estado vive en el SHELL y no en
  // la sección: los disparadores están repartidos entre los dos —la tarjeta de la
  // sidebar y la barra acá, el botón del encabezado en `HomeMinimal`— y si viviera
  // en la sección, el overlay se cerraría al cambiar de pantalla.
  const [motivoPricing, setMotivoPricing] = useState<MotivoPricing | null>(null);
  const abrirPricing = useCallback((motivo: MotivoPricing = 'mejorar') => {
    setMotivoPricing(motivo);
  }, []);

  // Marca activa REAL — misma fuente y mismo orden de preferencia que el editor
  // de marca (`config_sections/CompanyData`): el rubro de la marca activa
  // (`brand_profiles.rubro`) y, si no tiene, el de la empresa
  // (`company_profiles.rubro`). Antes era un ejemplo fijo, y el rubro del selector
  // podía no coincidir con el declarado en "Editar marca" (ADR-2026-07-31-rubro-selector-sale-marca-real).
  const {
    profile: perfilMarca,
    activeBrand: marcaActiva,
    loading: cargandoMarca,
    // Para el menú de marcas de la tarjeta: la lista, cuál está activa y el cambio.
    brands: marcas,
    currentBrandId: marcaActivaId,
    switchBrand: cambiarMarca,
    switching: cambiandoMarca,
  } = useBrandProfile();
  const marca = {
    nombre: marcaActiva?.name ?? perfilMarca?.company_name ?? MARCA_EJEMPLO.nombre,
    // `MARCA_EJEMPLO.rubro` NO es el respaldo de "esta marca todavía no eligió rubro".
    // Desde el alta por link invertida una cuenta recién creada no tiene ninguno
    // (`useBrandProfile` ya devuelve `null` en vez del centinela), y caer al ejemplo le
    // afirmaría "Hogar y decoración": un rubro que nunca dijo, que además decide qué
    // modos ve en la sidebar y con qué foto. El ejemplo queda para cuando no hay marca
    // NI perfil, o sea el preview sin sesión. El texto de la línea vacía lo pone el
    // render, más abajo, con `ETIQUETA_SIN_RUBRO`.
    rubro:
      marcaActiva || perfilMarca
        ? marcaActiva?.rubro ?? perfilMarca?.rubro ?? null
        : MARCA_EJEMPLO.rubro,
    // Logo de la marca ACTIVA — ADR-2026-08-04-la-marca-se-ve-con-su-logo. El
    // `company_logo` no es un fallback de "esta marca no tiene logo" sino de "esta
    // cuenta no tiene marcas": si hay marca activa y no tiene logo, va la inicial.
    // Caer a la empresa mostraría el logo de la marca vieja en las marcas nuevas —
    // es el mismo criterio que ya aplica el Sidebar de producción.
    logo: marcaActiva ? marcaActiva.logo_url : (perfilMarca?.company_logo ?? null),
  };

  // Cuenta REAL — ADR-2026-07-31-shell-nuevo-datos-reales. Identidad de `profiles`, plan/saldo/capacidad
  // de `company_profiles` y el descuento del mismo cupón invisible que aplica el
  // catálogo. Antes eran las constantes `PLAN_DEFAULT` / `CREDITOS_DEFAULT` /
  // `DESCUENTO_DEFAULT`, que servían mientras esto era un preview sin sesión.
  const cuentaReal = useCuentaAtlas();
  const { funciones: funcionesAvanzadas } = useFuncionesAvanzadas();

  /**
   * Los guards que `DashboardLayout` corre además de dibujar su chrome —
   * ADR-2026-08-05-el-shell-nuevo-si-corre-los-guards.
   *
   * `useShellGuards` se creó justamente para que los DOS shells corrieran lo mismo
   * (ADR-2026-07-31-shell-nuevo-urls-viejas), pero quedó llamado sólo desde
   * `DashboardLayout`: en desktop, las diez rutas del rediseño no lo ejecutaban.
   *
   * Lo que faltaba correr ahí:
   *  - El **guard de marca en borrador**. Una cuenta con el alta a medias entraba a la
   *    app y todas las queries filtran `is_draft = false`, así que veía la home vacía
   *    sin ninguna explicación en vez de volver a terminar su marca.
   *  - El **reset de `overflow`** en cada cambio de ruta, que destraba el scroll global
   *    cuando un overlay se cierra mal.
   */
  useShellGuards();

  /* Acá se aplicaba encima el contexto simulado del panel CONTEXTO DEV
     (ADR-2026-07-30-panel-contexto-dev-muda-esa): `dev.plan ?? cuentaReal.plan`, ídem
     créditos, descuento y capacidad. El panel y sus overrides se eliminaron al mergear
     `principal` (ADR-2026-08-05-fuera-los-overrides-de-dev), así que el shell muestra
     siempre la cuenta de verdad. */
  const plan = cuentaReal.plan;
  // Qué interfaz ve esta cuenta (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva). Se decide
  // acá y viaja por el contexto: la sidebar de abajo y la home leen el MISMO valor.
  const uiNueva = usaInterfazNueva(cuentaReal.grupoTesting);

  // Card flotante de ayuda al hover sostenido sobre los ítems de la sidebar —
  // compartido con la sidebar vieja (ver CardAyudaSidebar.tsx).
  const { abrirCardAyuda, cerrarCardAyuda, cardAyuda } = useCardAyudaSidebar();

  /**
   * La lista de herramientas ordenada según el plan: primero lo que se puede usar,
   * después Plantillas cerrando ese bloque, y al fondo lo bloqueado
   * (ADR-2026-08-03-plantillas-cierra-los-disponibles).
   *
   * El criterio de bloqueo es el MISMO que decide el candado y el click —
   * `getModePlanLock` sobre el plan real—, pasado como función para no repetirlo:
   * el orden, el ícono y el destino no pueden discrepar sobre la misma herramienta.
   */
  const herramientasOrdenadas = useMemo(
    () =>
      ordenarHerramientas(
        // Los dos modos de Plantillas van por rubro y son excluyentes: el de Moda
        // (ADR-2026-08-03-plantillas-solo-para-moda) y el de Belleza y Cuidado
        // Personal (ADR-2026-09-10-plantillas-belleza-modo-propio). Se FILTRA, no se
        // bloquea: un candado ofrece algo que se destraba pagando, y acá no hay nada
        // que destrabar — a los demás rubros el catálogo no les corresponde.
        HERRAMIENTAS_SIDEBAR.filter((h) => {
          if (h.id === 'plantillas') return rubroVePlantillas(marca.rubro);
          if (h.id === 'plantillas-belleza')
            return rubroVePlantillasBelleza(marca.rubro);
          // El resto sale de los FLAGS del catálogo y ya no de una lista de `id`
          // escrita a mano — ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares,
          // ADR-2026-09-09-la-sidebar-filtra-por-los-flags-del-catalogo.
          //
          // Acá estaban repetidos, por nombre, los mismos ejes que el catálogo ya
          // declara: `infografia`/`catalogo-muebles` son `soloHogarDeco`, y
          // `modelo`/`catalogo` son `ocultoEnHogarDeco`. Con la lista escrita a mano,
          // un modo nuevo con esos flags quedaba visible acá aunque el resto de la app
          // lo escondiera —fue exactamente lo que pasó con los tres modos nuevos de
          // Hogar y Decoración: se mostraban en cualquier rubro y el click rebotaba a
          // la home, porque la pantalla del modo sí gatea—. Y faltaba `devOnly` por
          // completo: ésta era la ÚNICA de las cuatro superficies que listaba los
          // modos internos a cualquiera.
          //
          // `HERRAMIENTAS_EXTRA` (Estudio de Video) no trae flags → pasa, que es lo
          // que corresponde: su gating es por plan, más abajo.
          return modoVisiblePorFlags(h, {
            isDevTester: cuentaReal.devTester,
            esHogarDeco: esRubroHogarDeco(marca.rubro),
            esBelleza: esRubroBelleza(marca.rubro),
            esModa: esRubroModa(marca.rubro),
          });
        }),
        (h) => !!getModePlanLock(MODE_PATH_TO_KIND[h.id] ?? h.id, plan),
      ),
    [plan, marca.rubro, cuentaReal.devTester],
  );

  /**
   * Accesos internos visibles — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
   *
   * Mismo orden de chequeos que el filtro de `components/Sidebar.tsx`: primero el
   * permiso, después `dev_tester`. Importa que sea ese orden y no al revés, porque las
   * claves `em-*` y `chat-atlas` están en `true` por defecto JUSTAMENTE para que el
   * corte lo dé `devOnly` y no el permiso (ver los comentarios de
   * `PermissionContext`); invertirlo cambiaría a quién le aparecen.
   *
   * Hoy `PermissionContext` devuelve SIEMPRE `defaultPermissions` —la tabla
   * `config_users` ya no existe y el fetch lo dice en un comentario—, así que este
   * chequeo es inerte y quien manda de verdad es `dev_tester`. Se consulta igual, y no
   * se simplifica a `devTester` a secas, para que el día que vuelva a haber permisos
   * por cuenta esta sidebar los respete sin que haya que acordarse de ella.
   */
  const { permissions } = usePermissions();
  const accesosInternos = useMemo(
    () =>
      cuentaReal.devTester
        ? ACCESOS_INTERNOS.filter((a) => permissions[a.permiso])
        : [],
    [permissions, cuentaReal.devTester],
  );

  /**
   * La nav de arriba, filtrada por permiso. Ver el comentario de `NAV_LATERAL` para
   * por qué "Inicio" no se gatea por grupo de testeo.
   */
  const navLateralVisible = useMemo(
    () =>
      NAV_LATERAL.filter(
        (item) =>
          (!('permiso' in item) || permissions[item.permiso]) &&
          // Las funciones avanzadas las prende la empresa; mientras el flag no
          // resolvió, el ítem NO se muestra (aparecer y desaparecer se ve peor
          // que aparecer tarde).
          (!('funcionAvanzada' in item) ||
            funcionesAvanzadas[item.funcionAvanzada as keyof typeof funcionesAvanzadas] === true) &&
          // Piso de plan del Agente. Mismo criterio que la línea de arriba: mientras el
          // plan no resolvió NO se muestra (ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
          // No parpadea en la práctica porque `plan` arranca de la semilla de
          // `localStorage`, igual que el resto de los gates del shell.
          (!('soloProUltra' in item) || puedeUsarElAgente(plan)),
      ),
    [permissions, funcionesAvanzadas, plan],
  );

  const creditos = cuentaReal.creditos;
  const descuento = cuentaReal.descuento;
  const planLabel = ETIQUETA_PLAN[plan] ?? plan;
  // Mismo criterio que la app real (ADR-2026-07-30-solo-criterio-saldo-toda-app): sin
  // créditos, o por debajo del 5% de la capacidad del plan. Vale para cualquier plan.
  //
  // La capacidad sale de `company_profiles.max_fotos_mes` cuando la fila la trae, y
  // sólo cae a `CAPACIDAD_POR_PLAN` si no: esa tabla es una aproximación por plan y a
  // un cliente con cupo a medida le movería el umbral del aviso.
  const capacidad = cuentaReal.capacidad ?? CAPACIDAD_POR_PLAN[plan];
  const estadoCreditos = estadoDeCreditos(creditos, capacidad);
  const estadoBarra: 'sin' | 'pocos' =
    estadoCreditos === 'ok' ? 'sin' : estadoCreditos;
  // Mientras la cuenta carga, `creditos` vale 0 y el estado daría "sin créditos":
  // la barra roja aparecería en cada entrada al shell y se iría sola un instante
  // después. Se espera a tener el dato.
  const mostrarBarra =
    !barraDescartada && !cuentaReal.cargando && estadoCreditos !== 'ok';
  // La medición va acá y no junto al `useRef`: depende de `mostrarBarra`, que se
  // resuelve recién en esta línea.
  useEffect(() => {
    const el = barraCreditos.current;
    if (!el) {
      setAltoBarraCreditos(0);
      return;
    }
    const medir = () => setAltoBarraCreditos(el.offsetHeight);
    medir();
    // El alto cambia sin desmontar: el copy de la barra se reacomoda en dos líneas
    // cuando la ventana se angosta (o cuando la sidebar se despliega).
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [mostrarBarra]);  // Criterio del CTA de mejora, una sola vez para los dos botones que lo usan.
  const planAlDia = plan !== 'gratis' && estadoCreditos === 'ok';

  /**
   * Cambiar de marca remonta la pantalla hospedada —
   * ADR-2026-08-03-cambiar-de-marca-remonta-la-pantalla.
   *
   * Cada pantalla resolvía por su cuenta qué hacer con `atlas:brand-changed`, y la
   * mayoría no hacía nada: la marca cambiaba en la sidebar y a la derecha seguía la
   * información de la anterior hasta navegar a otro lado y volver. En vez de sumarle
   * un listener a cada una, el shell le cambia la `key` al contenido: la pantalla se
   * desmonta y se monta de nuevo, así que vuelve a correr TODOS sus fetch de montaje
   * con la marca nueva, sepa o no del evento.
   *
   * El `nonce` no es `marcaActivaId`: al arrancar, ese valor pasa de `null` al id
   * real cuando resuelve el fetch de marcas, y usarlo de `key` remontaría la pantalla
   * una vez en cada carga —doble fetch de todo, y un parpadeo—. Se cuentan CAMBIOS,
   * no valores: la primera resolución sólo se anota.
   *
   * Va con el mismo fade que un cambio de sección (`FADE_SALIDA_MS`), y por eso el
   * remonte espera a que la salida termine: sin eso el contenido nuevo aparece de
   * golpe encima del viejo.
   */
  const nonceMarca = useNonceDeMarca(marcaActivaId, {
    retrasoMs: FADE_SALIDA_MS,
    alEmpezar: () => setVisible(false),
    alTerminar: () => setVisible(true),
  });

  const inicial = inicialDe(cuentaReal.nombreCompleto, cuentaReal.email);

  // Memo para no rehacer el objeto —y con él re-renderizar a todos los
  // consumidores— en cada render del shell.
  const cuenta = useMemo<CuentaCtx>(
    () => ({
      abrirPricing,
      creditos,
      planLabel,
      planAlDia,
      nombreCompleto: cuentaReal.nombreCompleto,
      email: cuentaReal.email,
      inicial,
      idCompany: cuentaReal.idCompany,
      // Sólo en cuentas de 2+ marcas (ADR-2026-08-03-creaciones-recientes-por-marca):
      // con una sola marca, filtrar por ella no distingue nada y sí esconde lo que el
      // join no puede resolver (generaciones sin producto, o con un producto sin
      // `brand_id`). Es la misma condición con la que el historial muestra su
      // dropdown de marcas.
      idMarca: marcaParaFiltrar(marcaActivaId, marcas.length > 1),
      // El rubro va SIEMPRE, sin la condición de 2+ marcas de `idMarca`: eso de arriba
      // es para no filtrar de más en creaciones recientes, y acá lo que se decide es
      // con qué foto se ve un modo, que aplica igual con una sola marca.
      rubroMarca: marca.rubro ?? null,
      // Literal: si este provider está en el árbol, el shell es quien monta.
      dentroDelShell: true,
      conFadeDeSalida,
      cargandoCuenta: cuentaReal.cargando,
      esDueno: cuentaReal.esDueno,
      uiNueva,
    }),
    [
      abrirPricing,
      creditos,
      planLabel,
      planAlDia,
      cuentaReal.nombreCompleto,
      cuentaReal.email,
      inicial,
      cuentaReal.idCompany,
      cuentaReal.cargando,
      cuentaReal.esDueno,
      uiNueva,
      marcaActivaId,
      marcas.length,
      marca.rubro,
      conFadeDeSalida,
    ],
  );

  return (
    // El provider envuelve TODO el shell —no sólo el `<Outlet />`— para que los
    // disparadores del overlay puedan estar de los dos lados: hoy los tres de la
    // sidebar y el del encabezado de la home (ADR-2026-07-31-creditos-mejora-plan-encabezado-home).
    <CuentaContext.Provider value={cuenta}>
    {/* `estilos-cursor-scope`: manito custom de la app sobre lo clickeable (ADR-2026-07-30-minimal-plantillas-reales-secciones-misma).
        Sin la clase, el cursor custom del <body> pinta la MISMA flecha sobre botones
        y fondo, y nada se siente clickeable. */}
    <div
      // `--shell-barra-creditos`: dónde termina la barra de créditos. La leen los
      // paneles de modo, que se montan `fixed` adentro de este árbol y arrancan
      // debajo de ella (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra).
      // Vale 0 cuando la barra no está.
      style={
        {
          '--shell-barra-creditos': `${altoBarraCreditos}px`,
        } as CSSProperties
      }
      // `relative`: con la sidebar en mínima el `<aside>` sale del flujo y se ancla a
      // esta caja (ver más abajo), así el contenido ocupa el ancho entero.
      className={`estilos-cursor-scope relative flex h-screen overflow-hidden font-sans text-[#16181D] ${tema.fondo}`}
    >
      {/* ── Sidebar flotante ────────────────────────────────────────────── */}
      {/* EL LOGO DE LA TIRA (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira): un
          botón aparte, flotando en la esquina donde estaba la sidebar, que la devuelve —
          como en el Estudio de Video, donde el logo de Atlas es la salida
          (ADR-2026-09-14-el-editor-tiene-dos-salidas-a-la-izquierda). Siempre montado,
          para que entre y salga con un fundido mientras la sidebar se cierra o se abre;
          apagado no se ve ni se toca. */}
      <button
        onClick={alternarSidebar}
        aria-label="Mostrar el menú"
        aria-hidden={!minima}
        tabIndex={minima ? 0 : -1}
        {...propsTooltip('Mostrar el menú')}
        // Una pastilla blanca con sombra y un chevron a la izquierda del logo
        // (ADR-2026-10-06-el-logo-de-la-tira-va-en-pastilla): suelto sobre el lienzo, el
        // logo solo no se leía como un botón. SUS MEDIDAS (`left-3`, `pl-2`, chevron de
        // 20, `gap-1`, logo de 20 de alto, `pr-3.5`) son las que suma
        // `ANCHO_LOGO_SIDEBAR_MINIMA`: si cambian acá, cambian allá, o los títulos de las
        // pantallas vuelven a quedar debajo de la pastilla
        // (ADR-2026-10-08-la-reserva-de-la-tira-mide-la-pastilla).
        className={`absolute left-3 top-4 z-30 inline-flex h-10 items-center gap-1 rounded-full bg-white pl-2 pr-3.5 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-[opacity,transform,background-color] duration-300 ease-out hover:bg-[#F3F4F8] ${
          minima ? 'opacity-100 scale-100 delay-150' : 'pointer-events-none opacity-0 scale-75'
        }`}
      >
        <span aria-hidden="true" className="material-symbols-rounded text-[20px] text-black/50">
          chevron_left
        </span>
        <img src="/images/estudio-atlas-logo-solo.svg" alt="Estudio Atlas" className="h-[20px] w-auto" />
      </button>
      <aside
        style={{ width: minima ? 0 : colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR }}
        // Las tres posiciones se animan con la MISMA caja: el ancho va de 244 a 66 a 0 y
        // el margen a cero con él, así el contenido acompaña sin saltos. En mínima el
        // borde se apaga (con ancho 0 se verían sus 2px) y lo de adentro se funde,
        // que es más suave que verlo recortarse. Sigue en el flujo: fuera de él, o
        // transparente, dejaba una franja del fondo del shell al costado del contenido.
        aria-hidden={minima}
        // `inert`: cerrada a cero no se ve, y con esto tampoco se tabula. Los tipos de
        // React 18 no lo conocen; el DOM sí.
        {...((minima ? { inert: '' } : {}) as Record<string, unknown>)}
        // Padding y borde también a cero en mínima: con `box-sizing: border-box` un ancho
        // de 0 no baja de padding + borde (26px), y eso era la franja de otro color que
        // seguía asomando al costado del lienzo.
        /* Escondida (`hideSidebar`, el Estudio de Video) va con `display:none`
           y no con el atributo `hidden`, que acá no haría nada: `flex` es una
           regla de autor y le gana al display del user-agent. Así no se ve, no
           ocupa lugar en el flex del shell y no toma foco. Es un caso más
           fuerte que "mínima", que sí se dibuja (ancho 0, se anima). */
        className={`${sidebarEscondida ? "hidden" : "flex"} shrink-0 flex-col overflow-hidden rounded-2xl transition-[width,margin,padding,opacity,border-width] duration-300 ease-out ${
          minima
            ? 'm-0 border-0 p-0 opacity-0 pointer-events-none'
            : 'm-2 border border-black/[0.06] px-3 py-4'
        } ${tema.panel}`}
      >
        <>
        <div
          className={`mb-5 flex items-center px-1.5 ${colapsada ? 'justify-center' : 'justify-between'}`}
        >
          {/* Expandida va el lockup horizontal (isotipo + marca); colapsada no hay
              ancho para la palabra y sólo queda el botón de expandir. */}
          {!colapsada && (
            <img
              src="/images/estudio-atlas-logo-horizontal.svg"
              alt="Estudio Atlas"
              className="h-[19px] w-auto"
            />
          )}
          {/* El toggle sólo donde hay ancho para expandir: abajo de 1024 la sidebar va
              colapsada sí o sí (ver `colapsada`, arriba). */}
          {anchoDeSobra && (
            <button
              onClick={alternarSidebar}
              aria-label={colapsada ? 'Expandir menú' : 'Colapsar menú'}
              // Desplegada sigue el `title` nativo: ahí el botón es el único ícono
              // suelto de la sidebar y no vale montar el tooltip propio para uno.
              title={colapsada ? undefined : 'Colapsar menú'}
              {...propsTooltip('Expandir menú')}
              className="grid h-7 w-7 place-items-center rounded-lg text-black/30 transition-colors hover:bg-black/[0.04] hover:text-black/60"
            >
              <span className="material-symbols-rounded text-[19px]">
                {colapsada ? 'left_panel_open' : 'left_panel_close'}
              </span>
            </button>
          )}
        </div>

        {/* La tarjeta despliega el menú de marcas en vez de ir a `/mi-marca`
            (ADR-2026-08-03-tarjeta-marca-abre-menu-de-marcas). El `unfold_more` que ya
            tenía prometía justamente esto. "Editar marca actual", adentro del menú,
            conserva el destino viejo — ahora abierto en "Información"
            (ADR-2026-08-05-editar-marca-abre-informacion), que es donde se editan los
            datos de la marca. La marca es la activa: `/mi-marca` trabaja siempre sobre
            ella, así que no hay nada que pasarle. */}
        <MenuMarcas
          marcas={marcas}
          marcaActivaId={marcaActivaId}
          cambiarMarca={cambiarMarca}
          cambiando={cambiandoMarca}
          onEditarMarca={() => irA('/mi-marca?tab=informacion')}
        >
        <span
          // Sin `aria-label`: el disparador accesible es el `<button>` que envuelve
          // este span (lo pone `MenuMarcas`), y ponerle nombre al span compite con él.
          // Sin rubro el tooltip es sólo el nombre: el template literal con `null`
          // adentro imprimiría la palabra "null".
          {...propsTooltip(
            marca.rubro ? `${marca.nombre} — ${marca.rubro}` : marca.nombre,
            { conAria: false },
          )}
          className={`mb-4 flex items-center rounded-xl py-2 text-left transition-colors hover:bg-black/[0.03] ${
            colapsada ? 'justify-center px-0' : 'gap-2.5 px-2'
          }`}
        >
          {/* 35px = alto exacto de la columna de al lado (18 del nombre + 2 del gap
              + 15 del rubro), para que el avatar arranque y termine con el bloque de
              texto — ADR-2026-07-31-tarjeta-marca-sidebar-alturas-atadas.
              `rounded-xl` (12px) y no `rounded-full`: el shell pasó a radio de 12px
              en avatares y pastillas, así que este sigue al resto. Ojo con `lg`, que
              en este config son 16px: a 35px de lado se ve casi igual de redondo.

              Mientras carga va el bloque gris y no la inicial, por lo mismo que los
              dos textos de al lado: la inicial que habría es la de la marca de
              ejemplo, y verla saltar a otra letra —o al logo— es peor que no ver
              nada (ADR-2026-08-04-la-marca-se-ve-con-su-logo). */}
          {cargandoMarca ? (
            <span className="h-[35px] w-[35px] shrink-0 rounded-xl bg-black/[0.07]" />
          ) : (
            <AvatarMarca
              logo={marca.logo}
              nombre={marca.nombre}
              className="h-[35px] w-[35px] shrink-0 rounded-xl"
              claseInicial="text-[12px]"
            />
          )}
          {!colapsada && (
            <>
              {/* Nombre y rubro apilados. `min-w-0` en la columna: sin eso el
                  `truncate` de los hijos no corta —un item de flex no baja de su
                  contenido— y el chevron se sale de la sidebar con nombres largos.

                  Mientras carga van dos barras grises en lugar de los textos: sin
                  eso se ve la marca de ejemplo y un instante después salta a la
                  real, que es peor que no mostrar nada. Miden lo mismo que las dos
                  líneas, así que el alto de la sidebar no se mueve. */}
              <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                {cargandoMarca ? (
                  <>
                    {/* Los márgenes verticales llevan cada barra al alto de la línea
                        que reemplaza (12+3+3=18 y 10+2,5+2,5=15) para que el bloque
                        mida los mismos 35px cargando y cargado. */}
                    <span className="my-[3px] h-3 w-24 rounded bg-black/[0.07]" />
                    <span className="my-[2.5px] h-2.5 w-16 rounded bg-black/[0.05]" />
                  </>
                ) : (
                  <>
                    <span className="truncate text-[13.5px] font-[500] leading-[18px]">
                      {marca.nombre}
                    </span>
                    {/* Con `ETIQUETA_SIN_RUBRO` y no vacío: el alto del avatar está atado
                        a estas dos líneas (18+2+15=35), y un span sin contenido mide 0 y
                        desalinea la tarjeta entera. */}
                    <span className="truncate text-[11.5px] font-[400] leading-[15px] text-black/45">
                      {marca.rubro ?? ETIQUETA_SIN_RUBRO}
                    </span>
                  </>
                )}
              </span>
              <span className="material-symbols-rounded text-[16px] text-black/30">unfold_more</span>
            </>
          )}
        </span>
        </MenuMarcas>

        {/* `gap-1.5` = 6px entre ítems (ADR-2026-07-31-columna-contenido-90-mejorar-plan). */}
        <nav className="flex flex-col gap-1.5">
          {navLateralVisible.map((item) => (
            <EnlaceApp
              key={item.label}
              href={item.ruta}
              onNavegar={() => {
                cerrarCardAyuda();
                irA(item.ruta);
              }}
              // Ítems CON descripción llevan el card de ayuda propio (sin
              // title nativo ni tooltip: taparían al card); los demás usan el
              // tooltip de sidebar colapsada. `in` y no `.descripcion` por el
              // `as const` de NAV_LATERAL (mismo patrón que `permiso`).
              {...('descripcion' in item
                ? {
                    onMouseEnter: (e: ReactMouseEvent<HTMLElement>) =>
                      abrirCardAyuda(
                        e.currentTarget,
                        item.label,
                        item.descripcion,
                      ),
                    onMouseLeave: cerrarCardAyuda,
                  }
                : propsTooltip(item.label))}
              className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
              } ${
                // Seleccionado: ícono y texto en el azul de marca, con el fondo que
                // le corresponda a la variante (tinte azul en clara, blanco en gris).
                // Sale de la ruta actual y ya no de `i === 0`: con la nav navegando
                // de verdad entre tres pantallas (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo), fijarlo en el primero
                // dejaba "Inicio" marcado estando en Creaciones.
                item.ruta === pathname
                  ? `${tema.seleccion} ${ITEM_ACTIVO}`
                  : `${GRIS_SHELL} hover:bg-black/[0.03]`
              }`}
            >
              {/* Sin `opacity-75` propia: el ícono toma el mismo negro al 50% que la
                  etiqueta. Con la opacidad encima quedaba al 37,5% y los íconos se
                  leían más apagados que su texto. */}
              <span className="material-symbols-rounded shrink-0 text-[19px]">
                {item.icono}
              </span>
              {!colapsada && item.label}
            </EnlaceApp>
          ))}
        </nav>

        {/* Divisor: 16px arriba, 22px abajo, al 80% del ancho y alineado a la
            izquierda (ADR-2026-07-31-divisor-sidebar-16-22-aire). Sin `mx-auto` el flex column lo deja arrancando en
            el borde izquierdo del contenido, que ya trae el `px-3` de la sidebar. */}
        {/* El aire de ABAJO vuelve a 20px (había bajado a 12 al pasar a dos grupos —
            ADR-2026-08-31-mas-aire-entre-el-divisor-y-el-primer-grupo, refina
            ADR-2026-07-31-divisor-sidebar-16-22-aire). Con 12 el rótulo del primer
            grupo quedaba colgando del divisor: se leían como una sola pieza en vez de
            un corte y, después, el encabezado de una lista. Los 16 de arriba no se
            tocan, que es lo que despega el divisor de "Marca". */}
        <div className="mb-5 ml-3 mt-4 h-px w-4/5 bg-black/[0.06]" />

        {/* Las secciones de modos: cada una abre un panel con los suyos
            (ADR-2026-10-05-la-sidebar-por-secciones). Los nombres salen de `COPY_MODOS`,
            como en el resto de la app. */}
        {/* `gap-1.5` = 6px, el MISMO que la nav de arriba (ADR-2026-07-31-columna-contenido-90-mejorar-plan): las dos listas
            son ítems de navegación del mismo peso, y separarlas distinto hacía que
            la de herramientas se leyera como un bloque más apretado. */}
        {/* `overflow-x-hidden` A PROPÓSITO: con `overflow-y-auto` solo, CSS vuelve el eje
            X también `auto`, y colapsada (42px útiles) la barra vertical se llevaba ~15 y
            los íconos de 36 desbordaban a lo ancho: salía una mini barra horizontal
            (ADR-2026-09-16-la-sidebar-colapsada-no-scrollea-a-lo-ancho). Colapsada se
            esconde además la barra vertical, que en ese ancho tapaba los íconos; la lista
            sigue scrolleando con la rueda. */}
        <nav
          className={`flex flex-col gap-1.5 overflow-x-hidden overflow-y-auto ${
            colapsada ? 'scrollbar-hide' : ''
          }`}
        >
          {/* Las dos sidebars conviven (ADR-2026-10-07-las-dos-sidebars-conviven): el grupo
              C ve las tres secciones con panel (ADR-2026-10-05-la-sidebar-por-secciones:
              qué modo va en cada una sale de `seccionesDeModos`, la misma lista de los
              popups de la home); el resto, los grupos Imagen / Video de siempre con
              "Generar…" y los dos modos recientes (`GruposDeLaSidebar`). Las dos reciben
              el candado, el click con candado y las clases de la fila de acá, para que
              digan lo mismo de lo mismo. */}
          {uiNueva ? (
            <SeccionesDeLaSidebar
              herramientas={herramientasOrdenadas}
              ediciones={EDICIONES_RAPIDAS}
              estaBloqueada={(path) => !!getModePlanLock(MODE_PATH_TO_KIND[path] ?? path, plan)}
              edicionBloqueada={(e) => !!candadoEdicionRapida(e, plan)}
              rubroMarca={marca.rubro ?? null}
              pathname={pathname}
              colapsada={colapsada}
              irA={irA}
              // Con candado no se navega: el catálogo de planes, como el resto de la sidebar
              // (ADR-2026-08-03-candado-de-sidebar-abre-el-catalogo).
              alBloqueada={() => abrirPricing('bloqueado')}
              claseFila={(activa) =>
                `flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                  colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                } ${activa ? `${tema.seleccion} ${ITEM_ACTIVO}` : `${GRIS_SHELL} hover:bg-black/[0.03]`}`
              }
              propsTooltip={propsTooltip}
              alAbrir={cerrarCardAyuda}
            />
          ) : (
            <GruposDeLaSidebar
              herramientas={herramientasOrdenadas}
              estaBloqueada={(path) => !!getModePlanLock(MODE_PATH_TO_KIND[path] ?? path, plan)}
              pathname={pathname}
              colapsada={colapsada}
              irA={irA}
              alBloqueada={() => abrirPricing('bloqueado')}
              // Con candado, la fila va más tenue (`text-black/40`): es una oferta, no
              // navegación (ADR-2026-08-03-sidebar-candado-modos-sin-plan).
              claseFila={(activa, bloqueada) =>
                `flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                  colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                } ${
                  activa
                    ? `${tema.seleccion} ${ITEM_ACTIVO}`
                    : bloqueada
                      ? 'text-black/40 hover:bg-black/[0.03]'
                      : `${GRIS_SHELL} hover:bg-black/[0.03]`
                }`
              }
              propsTooltip={propsTooltip}
              abrirCardAyuda={abrirCardAyuda}
              cerrarCardAyuda={cerrarCardAyuda}
            />
          )}

          {/* Accesos internos (`dev_tester`) — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
              Van DENTRO de esta misma `<nav>` y no en una propia para que scrolleen
              con las herramientas: son la continuación de la lista, y una segunda
              caja con su propio scroll parte el recorrido en dos.
              `accesosInternos` ya viene vacío para quien no es dev tester, así que
              para un cliente esto no dibuja ni un nodo. */}
          {accesosInternos.map((a) => {
            const activa =
              pathname === a.ruta ||
              pathname.startsWith(`${a.ruta}/`) ||
              (a.rutasActivas?.some(
                (r) => pathname === r || pathname.startsWith(`${r}/`),
              ) ??
                false);
            return (
              <Fragment key={a.ruta}>
                {/* Mismo rótulo que "Todas las herramientas": es un encabezado de
                    grupo, no un ítem. Colapsada no hay ancho para la palabra. */}
                {a.abreGrupo && !colapsada && (
                  <p className="mb-2 mt-3 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
                    {a.abreGrupo}
                  </p>
                )}
                <EnlaceApp
                  href={a.ruta}
                  onNavegar={() => {
                    cerrarCardAyuda();
                    irA(a.ruta);
                  }}
                  title={colapsada && !a.descripcion ? a.label : undefined}
                  onMouseEnter={
                    a.descripcion
                      ? (e) =>
                          abrirCardAyuda(e.currentTarget, a.label, a.descripcion!)
                      : undefined
                  }
                  onMouseLeave={a.descripcion ? cerrarCardAyuda : undefined}
                  className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                    colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                  } ${
                    activa
                      ? `${tema.seleccion} ${ITEM_ACTIVO}`
                      : `${GRIS_SHELL} hover:bg-black/[0.03]`
                  }`}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px]">
                    {a.icono}
                  </span>
                  {!colapsada && <span className="truncate">{a.label}</span>}
                </EnlaceApp>
              </Fragment>
            );
          })}
        </nav>

        {/* Card flotante de ayuda de los ítems de arriba (portal a body). */}
        {cardAyuda}

        {/* Pie: soporte y, cerrando la sidebar, la tarjeta de plan + créditos.
            El perfil no va como ítem suelto: es el botón de usuario de la tarjeta. */}
        <div className="mt-auto pt-4">
          {/* Campana — ADR-2026-08-31-centro-de-notificaciones. También acá y no
              sólo en la fila de cuenta de la home: esa fila SÓLO existe en la
              home, así que con la campana únicamente ahí el badge sería invisible
              desde el resto de la app, que es donde el usuario pasa el tiempo.
              A diferencia de la del encabezado, ésta NO abre panel: lleva derecho a
              `/notificaciones` (ADR-2026-08-31-campana-de-sidebar-navega-a-la-pantalla),
              como el resto de los ítems de esta columna. */}
          <CampanaNotificaciones
            variante="sidebar"
            colapsada={colapsada}
            onNavegar={(ruta) => irA(ruta)}
          />

          {/* "Invitá y ganá" (ADR-2026-08-27-programa-referidos). Va en el pie y no
              entre las herramientas: no es una forma de generar contenido, es una
              sección de la cuenta. Desde que "Centro de ayuda" se fue al menú del
              avatar (ADR-2026-09-21-el-centro-de-ayuda-sale-de-la-sidebar) es el único
              ítem de acá abajo — el que le OFRECE algo al usuario, que es por lo que
              ya era el primero de los dos. */}
          <EnlaceApp
            href="/referidos"
            onNavegar={() => irA('/referidos')}
            {...propsTooltip('Invitá y ganá')}
            className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors hover:bg-black/[0.03] hover:text-black/85 ${GRIS_SHELL} ${
              colapsada ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-2.5 px-2.5'
            }`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">redeem</span>
            {!colapsada && 'Invitá y ganá'}
          </EnlaceApp>

          {/* Acá vivía "Centro de ayuda", el ítem que cerraba el pie con un enlace a
              `/ayuda` — ADR-2026-09-21-el-centro-de-ayuda-sale-de-la-sidebar. Se fue de
              la sidebar y no de la app: sigue en el menú del avatar
              (`ITEMS_MENU_CUENTA`), que además es la única puerta que tiene en mobile,
              así que `/ayuda` no queda huérfana. */}

          {/* Tarjeta de cuenta: plan vigente, créditos restantes y el CTA de mejora.
              El contador usa `token_icon.png`, el ícono de créditos oficial de la app
              (el mismo del Sidebar real y de `CreditBadge`). */}
          {colapsada ? (
            <div className="mt-2 flex flex-col items-center gap-1.5">
              <MenuCuenta variante="sidebar">
                <AvatarUsuario />
              </MenuCuenta>
              <button
                onClick={() => abrirPricing('mejorar')}
                aria-label="Ver planes"
                {...propsTooltip('Ver planes')}
                className="grid h-9 w-9 place-items-center rounded-xl bg-brand-primary text-white transition-colors hover:bg-brand-primary-hover"
              >
                <span className="material-symbols-rounded text-[19px]">bolt</span>
              </button>
            </div>
          ) : (
            // Blanca en las dos variantes: en la clara se separa del panel por el
            // borde, y en la gris es justamente la figura sobre el panel gris.
            //
            // La tarjeta ENTERA despliega el menú de cuenta, el mismo que el avatar
            // de arriba a la derecha. Es un `div` y no un `button` porque adentro hay
            // otros dos controles —el avatar y "Mejorar plan"—, y anidar botones es
            // HTML inválido; por eso dispara el menú por handle en vez de envolverlo.
            //
            // No lleva `role="button"` ni `tabIndex`: un control que contiene otros
            // controles se anuncia mal y atrapa el foco. El click de la tarjeta es una
            // comodidad de puntero; el acceso por teclado y para lectores de pantalla
            // sigue siendo el avatar, que sí es un botón con `aria-haspopup`.
            <div
              ref={tarjetaCuenta}
              onClick={() => menuCuentaSidebar.current?.alternar()}
              className="mt-2 cursor-pointer rounded-2xl border border-black/[0.09] bg-white p-3 transition-colors hover:border-black/[0.14]"
            >
              <div className="flex items-center gap-2.5">
                {/* `contents`: el wrapper existe SÓLO para frenar la propagación, así
                    que no participa del layout y la fila queda igual que antes. */}
                <span className="contents" onClick={(e) => e.stopPropagation()}>
                  <MenuCuenta
                    ref={menuCuentaSidebar}
                    variante="sidebar"
                    zona={tarjetaCuenta}
                  >
                    <AvatarUsuario />
                  </MenuCuenta>
                </span>
                {/* Plan y saldo un escalón por debajo de la navegación (ADR-2026-07-31-plan-saldo-escalon-debajo-tarjeta): son
                    el estado de la cuenta, no una acción. El que manda en la tarjeta es
                    el botón de abajo. */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold leading-tight">
                    {planLabel}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-[12px] font-semibold leading-none text-brand-primary">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="h-3 w-3"
                    />
                    <span className="tabular-nums">{creditos}</span>
                  </p>
                </div>
                {/* Acá iba un botón de perfil (ADR-2026-07-31-engranaje-tarjeta-cuenta-pasa-icono). Se sacó en ADR-2026-07-31-fuera-boton-perfil-tarjeta-cuenta: desde
                    que la tarjeta ENTERA lleva al perfil (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil), era un tercer
                    control apretado en la misma fila para el mismo destino.
                    El acceso por teclado no se pierde: el ítem "Perfil" del menú que
                    abre el avatar de la izquierda va al mismo lado. */}
                {/* El mismo `unfold_more` que la tarjeta de marca: desde que la tarjeta
                    despliega un menú (y ya no navega), nada lo anunciaba y se leía como
                    un bloque de información. No es un botón —el control es la tarjeta
                    entera—, así que va como ícono suelto. */}
                <span
                  aria-hidden="true"
                  className="material-symbols-rounded shrink-0 text-[16px] text-black/30"
                >
                  unfold_more
                </span>
              </div>
              {/* Sólido sólo cuando hay algo que resolver: plan gratis, o saldo en
                  cero / por debajo del umbral (ADR-2026-07-31-columna-contenido-90-mejorar-plan). El criterio vive en
                  `clasesMejorarPlan`, compartido con el CTA del encabezado. */}
              <button
                onClick={(e) => {
                  // Sin esto el click sube a la tarjeta y despliega el menú de cuenta
                  // en vez de abrir el catálogo.
                  e.stopPropagation();
                  abrirPricing('mejorar');
                }}
                className={`mt-3 w-full rounded-xl py-2.5 text-[12.5px] font-semibold transition-colors ${clasesMejorarPlan(planAlDia)}`}
              >
                {/* "Ver planes" y no "Mejorar plan": el botón abre el catálogo de planes
                    —donde se compara y se elige—, y lo ve también quien ya está en el plan
                    más alto (ADR-2026-09-04-el-cta-de-planes-dice-ver-planes). El color
                    sigue diciendo si hay algo que resolver: `clasesMejorarPlan` lo pinta
                    sólido con plan gratis o saldo en cero. */}
                Ver planes
              </button>
            </div>
          )}
        </div>
        </>
      </aside>

      {/* El tooltip de la sidebar colapsada. Se monta por portal en el `body`
          (`useTooltipsSidebar`), así que da igual dónde vaya esta línea; queda al lado
          del `<aside>` porque es de él. */}
      {tooltipSidebar}

      {/* ── Contenido ─────────────────────────────────────────────────────
          `content-panel-clip` NO tiene CSS: es el hook por el que
          `utils/appScroll` y `ScrollToTop` encuentran el scroller de la app
          (ADR-171). Poniéndoselo acá, las pantallas que se montan adentro
          conservan su guardado/restaurado de scroll y el reset al cambiar de
          ruta sin tocarles una línea. `shell-home-scroll` reinterpreta sus
          `min-h-screen` (ver index.css). Ambos son ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo. */}
      <main className="relative flex-1 overflow-y-auto content-panel-clip shell-home-scroll">
        {/* La barra de créditos vive en el SHELL, arriba del contenido: es del
            usuario, no de la sección, así que no se va ni reaparece al cambiar de
            pantalla.
            Va en la MISMA columna que el contenido de las pantallas: el 90% centrado
            del wrapper de abajo más sus 32px de sangría
            (ADR-2026-08-03-barra-creditos-vuelve-a-la-columna, que supersede
            ADR-2026-08-03-barra-creditos-margenes-fijos-32 y restituye
            ADR-2026-07-30-barra-creditos-alinea-columna-contenido). Los 32px pelados
            se eligieron cuando las pantallas hospedadas ponían su propia sangría
            fija; desde ADR-2026-08-03-sangria-32-la-pone-el-shell la sangría es de
            este wrapper, o sea 5% + 32px, y la barra era lo único que no la seguía.
            El `mx-auto max-w-[90%]` es el del wrapper y el `px-8` su sangría: los dos
            juntos, no uno u otro. */}
        {mostrarBarra && (
          // `sticky top-0` — ADR-2026-08-04-la-barra-de-creditos-no-scrollea. La barra
          // vivía en el flujo de `<main>`, que ES el scroller de la app: al bajar se
          // iba con el contenido, y el aviso de saldo desaparecía justo cuando el
          // usuario estaba trabajando. Ahora se queda arriba de todo.
          //
          // La franja va a ANCHO COMPLETO y la columna queda adentro: el fondo del
          // lienzo es lo que tapa el contenido que pasa por debajo, y con la franja
          // recortada al 90% ese contenido asomaría por los costados.
          //
          // Los 10px de aire de arriba pasaron de `mt-2.5` en la barra a `pt-2.5` acá:
          // un margen colapsa hacia afuera del wrapper y quedaría FUERA de la caja
          // pegada —o sea, contenido pasando por ese hueco—, y además es lo que hace
          // que `offsetHeight` mida la franja entera (ver la medición más arriba).
          <div
            ref={barraCreditos}
            className={`sticky top-0 z-30 pt-2.5 ${tema.fondo}`}
          >
          <div className="mx-auto max-w-[90%] px-8">
            <CreditsTopBar
              className="rounded-2xl"
              estado={estadoBarra}
              restantes={creditos}
              discountPercent={descuento}
              onDismiss={() => setBarraDescartada(true)}
              // El CTA abre el mismo overlay, con el copy del estado que la barra
              // acaba de anunciar — así el catálogo no contradice al cartel que lo
              // abrió (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo).
              onUpgrade={() => abrirPricing(estadoBarra)}
            />
          </div>
          </div>
        )}

        {/* Sólo ESTO se desvanece al cambiar de sección: la sidebar y la barra
            quedan quietas (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El `pb-6` (24px, ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo) es el aire de abajo
            para TODAS las secciones — las pantallas hospedadas traen `md:pb-0`
            porque contaban con el padding que ponía `.main-content`, que este
            shell no tiene, y sin esto terminan pegadas al borde.

            `max-w-[90%] mx-auto` es la columna de contenido de las TRES secciones
            (ADR-2026-07-31-columna-contenido-90-mejorar-plan): puesta acá y no en cada pantalla, la barra de créditos —que
            usa el mismo ancho— queda alineada con lo que hay debajo, sin que las
            pantallas hospedadas tengan que enterarse. El 5% de cada lado hace de
            padding lateral, así que la home ya no lleva el suyo. */}
        {/* Hospedaje NEUTRO para las pantallas mudadas desde `DashboardLayout` —
            ADR-2026-08-20-el-shell-hospeda-en-neutro-lo-que-no-nacio-para-la-columna.

            Esas 13 no se escribieron para este shell: contaban con el panel de
            `.main-content`, o sea ancho completo, alto definido y su propio padding.
            Metidas en el wrapper de abajo se les rompen tres cosas a la vez — el
            `padding: 0 !important` de `.shell-seccion` les come la sangría, el `h1`
            se les va a 32px, y —la peor— el `md:min-h-full` del Estudio de marketing
            deja de resolver, porque su contenedor pasaría a ser un div de alto `auto`
            en vez del panel.

            Acá NO se envuelve en NADA: el `<Outlet />` cuelga directo de `<main>`,
            que es un flex item con alto definido, igual que colgaba del panel fijo de
            `DashboardLayout`. Es el hospedaje que menos se parece a una decisión.

            Se pierde el fade de entrada en esas 13, y está bien: el fade es de las
            secciones nativas del shell, y un wrapper puesto sólo para animarlas es
            justamente el div de alto `auto` que rompe el alto. */}
        {hospedajeNeutro ? (
          <SidebarContext.Provider value={ctxShell}>
            <Outlet key={nonceMarca} />
          </SidebarContext.Provider>
        ) : (
        <div
          className={`shell-seccion mx-auto max-w-[90%] pb-6 transition-opacity ${
            visible
              ? 'opacity-100 duration-[680ms] ease-out'
              : 'opacity-0 duration-[240ms] ease-in'
          }`}
        >
          <SidebarContext.Provider value={ctxShell}>
            {/* La `key` es lo que remonta la pantalla al cambiar de marca
                (ADR-2026-08-03-cambiar-de-marca-remonta-la-pantalla). Va en el
                `Outlet` y NO en el `<div>` de arriba a propósito: ese div es el que
                hace el fade, y si se remontara nacería con `opacity-100` y la
                transición no tendría de dónde arrancar. */}
            <Outlet key={nonceMarca} />
          </SidebarContext.Provider>
        </div>
        )}
      </main>

      {/* Columna del chat de Atlas — ADR-2026-08-20-el-chat-viaja-con-las-pantallas-que-lo-abren
          y ADR-2026-09-08-la-columna-del-chat-deja-de-depender-del-hospedaje.

          `PanelChatAtlas` vivía SÓLO en `DashboardLayout`. Las seis pantallas del
          Estudio de marketing tienen un botón "Pregúntale a Atlas"
          (`BotonPreguntaleAtlas`) que llama a `abrirChat()`, así que al mudarlas a este
          shell el botón habría prendido el estado global sin que se dibujara nada.

          Iba atado a `hospedajeNeutro`, que era exacto por coincidencia: las rutas
          mudadas eran justo las que traían el botón. En cuanto una sección NATIVA sumó
          el botón (Pre-lanzamientos) pasó lo que este comentario anticipaba y tocarlo no
          hacía nada. Ahora la pregunta es propia: `rutaConChatAtlas` dice quién dibuja la
          columna, y `hospedajeNeutro` sigue diciendo cómo se hospeda — que son cosas
          distintas y meter `/lanzamientos` en la lista legacy le habría roto el layout.

          Desde ADR-2026-09-10-la-columna-del-chat-esta-en-todas-las-rutas ya no hay
          lista: el flotante "Preguntále a Atlas" abre el chat desde CUALQUIER pantalla, y
          una columna que no se dibuja es un botón que prende el estado sin mostrar nada
          (exactamente lo que se reportó: "no lo estoy viendo"). Cerrada, la columna no
          cuesta nada —el espaciador mide 0 y el panel devuelve null—, y en `/chat` la
          esconde `useEmpujeChat`. */}
      <SidebarContext.Provider value={ctxShell}>
        <ColumnaChatShell />
      </SidebarContext.Provider>

      {/* Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). Se portalea a `document.body`
          desde adentro del componente, así que da igual dónde se monte acá: no
          queda atrapado en el stacking context del shell. */}
      {motivoPricing && (
        <CreditsPricingSheet
          {...copyPricingPorMotivo(motivoPricing)}
          onClose={() => setMotivoPricing(null)}
        />
      )}
    </div>
    </CuentaContext.Provider>
  );
}

/**
 * La home vive en `HomeMinimal.tsx`, al lado de este archivo.
 *
 * Estuvo acá hasta ADR-2026-09-01-la-version-2-es-la-home: la home que ganó la
 * comparación de variantes salió del sandbox como un archivo propio, así que en vez de
 * volcar sus ~450 líneas —dos franjas, un popup y sus ilustraciones— dentro de este
 * archivo, que ya trae el shell entero, se promovió el archivo tal cual.
 *
 * Lo que SÍ sigue acá es lo que la home comparte con el resto del shell: el esqueleto
 * (`HomeMinimalSkeleton`, abajo), el cuerpo de secciones (`SeccionesHome`) y los
 * ayudantes de copy y tema que importa.
 */
/**
 * Skeleton de la home — ADR-2026-08-03-home-aparece-entera-o-skeleton.
 *
 * Es el ESQUELETO DE ESTA home y no el `HomeSkeleton` de `HomeCards`: ese dibuja la
 * home vieja (saludo centrado, dos cards grandes) y usarlo acá cambiaría de layout a
 * mitad de la carga, que es justo lo que se está sacando.
 *
 * Las medidas están copiadas de los componentes reales —los 120px de las novedades,
 * los 230px de las cards de vertiente, la fila de saludo, el alto del cartel— para que
 * al terminar la carga nada se mueva de lugar. **Si cambia el layout de arriba del
 * pliegue en `HomeMinimal.tsx`, tiene que cambiar acá.**
 */
// Exportado porque la home vive en su propio archivo (`HomeMinimal.tsx`,
// ADR-2026-09-01-la-version-2-es-la-home) y es ella la que lo monta mientras carga.
/**
 * `uiNueva`: el esqueleto promete la cabecera que después se va a ver
 * (ADR-2026-08-03-home-aparece-entera-o-skeleton): las tres cards por destino para el
 * grupo C, las dos franjas + las dos cards de vertiente para el resto
 * (ADR-2026-10-07-la-home-elige-su-cabecera).
 */
export function HomeMinimalSkeleton({ uiNueva = false }: { uiNueva?: boolean } = {}) {
  const bloque = 'rounded-xl bg-black/[0.06] animate-pulse';
  // El esqueleto tiene que prometer lo que después se va a ver: si "Creaciones
  // recientes" no le corresponde a esta cuenta, su fila tampoco se dibuja acá — si no,
  // la carga muestra un hueco que desaparece y el resto de la home sube de golpe.
  //
  // Es el MISMO hook que usa la sección
  // (ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda) y no una copia de la
  // condición: acá había un `isPaidPlan(usePlanUsuario())` propio, o sea la regla escrita
  // dos veces. Con el corte pasando de "plan" a "plan + rubro", dos copias es la receta
  // para que el esqueleto y la sección discrepen — que es exactamente el parpadeo que
  // este `if` viene a evitar.
  const seVenCreaciones = useMuestraCreacionesRecientes();
  return (
    <div aria-hidden="true">
      {/* Saludo + accesos de cuenta: la misma fila de dos columnas.
          Las barras del saludo van DENTRO de un `<h1>`, y no es decorativo: los 40px
          de aire de arriba los pone una regla del shell que selecciona por
          `:has(> h1)` (`.shell-seccion`, ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo).
          Sin el `<h1>` la regla no matcheaba y el esqueleto arrancaba 40px más arriba
          que el contenido real. Copiar el `pt-10` a mano habría funcionado hasta que
          alguien cambie la regla; así los dos estados leen la misma fuente.
          Van `<span class="block">` y no `<div>` porque un `<h1>` espera contenido de
          frase. Los altos suman 77px, que es lo que mide el saludo real de dos
          renglones — medido, no calculado: la cuenta de `font-size × line-height` da
          69,6 y el navegador termina en 77. */}
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <h1 className="w-full max-w-[420px]">
          <span className={`block h-[38px] w-[62%] ${bloque}`} />
          <span className={`mt-2 block h-[31px] w-[48%] ${bloque}`} />
        </h1>
        <div className="flex shrink-0 items-center gap-2.5">
          <div className={`h-[34px] w-[150px] ${bloque}`} />
          <div className={`h-[34px] w-[112px] ${bloque}`} />
          <div className={`h-[34px] w-[34px] rounded-full bg-black/[0.06] animate-pulse`} />
        </div>
      </div>

      {/* Cartel. Va SIEMPRE, aunque después no corresponda ninguno: es el que tarda,
          y dejarle el lugar es la mitad del arreglo. Si no va, lo que pasa al
          terminar es que lo de abajo sube — no que aparezca algo empujando. */}
      <div className={`mt-12 h-[92px] rounded-2xl bg-black/[0.06] animate-pulse`} />

      {uiNueva ? (
        <>
          {/* Secciones: el título chico y las tres cards, en la misma grilla y con el
              mismo alto que `CardSeccion` (una columna de 268px hasta `lg`, tres
              cuadradas desde ahí). */}
          <div className="mt-8">
            <div className={`h-[22px] w-44 ${bloque}`} />
            <div className="mt-3 grid grid-cols-1 gap-2.5 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-[268px] rounded-2xl bg-black/[0.06] animate-pulse lg:aspect-square lg:h-auto`} />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Novedades: título chico + dos franjas bajas de 120px.
              Van SIEMPRE, aunque un usuario sin acceso a los dos modos nuevos vea menos:
              es la franja más barata de reservar y la que menos se mueve si falta una. */}
          <div className="mt-12">
            <div className={`h-[22px] w-32 ${bloque}`} />
            <div className="mt-3 flex gap-2.5">
              {[0, 1].map((i) => (
                <div key={i} className={`h-[120px] flex-1 rounded-2xl bg-black/[0.06] animate-pulse`} />
              ))}
            </div>
          </div>

          {/* Vertientes: los mismos 230px de alto y el mismo gap que `CardVertiente`. */}
          <div className="mt-8">
            <div className={`h-[22px] w-44 ${bloque}`} />
            <div className="mt-3 flex gap-2.5">
              {[0, 1].map((i) => (
                <div key={i} className={`h-[230px] flex-1 rounded-2xl bg-black/[0.06] animate-pulse`} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ediciones rápidas: el título grande, la bajada y las siete filas en 4×2.
          Cada fila es la pastilla de 46px y dos renglones de texto al lado, que es lo
          que mide `EdicionesRapidasHero`. */}
      <div className="mt-10">
        <div className={`h-[39px] w-[220px] ${bloque}`} />
        <div className={`mt-1 h-[22px] w-[300px] ${bloque}`} />
        <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3.5">
              <div className={`h-[46px] w-[46px] shrink-0 rounded-2xl bg-black/[0.06] animate-pulse`} />
              <div className="min-w-0 flex-1">
                <div className={`h-[17px] w-[62%] ${bloque}`} />
                <div className={`mt-1.5 h-[14px] w-[86%] ${bloque}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Primera sección de abajo (creaciones recientes): título + fila de cards. */}
      {seVenCreaciones && (
        <div className="mt-12">
          <div className={`h-6 w-48 ${bloque}`} />
          <div className="mt-4 grid grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`aspect-[4/5] rounded-2xl bg-black/[0.06] animate-pulse`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Cierre de la sección Plantillas — el acceso al catálogo completo al PIE de la
 * sección, no sólo en el "Ver todo" del encabezado.
 *
 * La home muestra 3 de las ~10 categorías (ADR-2026-07-31-plantillas-home-rotan-tres) y
 * hasta acá lo único que llevaba al catálogo entero era el "Ver todo" chico del
 * título: a la altura donde el usuario TERMINA de mirar las filas —tres tarjetas más
 * abajo— no quedaba ningún camino, y la sección se leía como si eso fuera todo el
 * catálogo. El pie de una lista parcial es justo donde aparece la pregunta que este
 * bloque contesta.
 *
 * **Es la anatomía que tenían los carteles promocionales de home**: tarjeta blanca
 * con borde, ícono de marca + título en una línea, y CTA suave (lavanda con texto
 * azul) a la derecha, reducido a la flecha en mobile. Es lo que este bloque ES —una
 * sugerencia al pie, no algo que haya que resolver—, y por eso se queda como está
 * aunque arriba ya no haya ningún cartel con esta forma: los dos que quedaban son la
 * tarjeta de configuración, que es sólida y teñida justo porque NO es una sugerencia
 * (ADR-2026-08-27-tarjeta-configuracion-cuenta-paga).
 *
 * Toda la tarjeta es el botón (la pastilla es decorativa, con `group-hover`): con
 * texto clickeable + botón al lado, el 80% del área no hacía nada. Los carteles de
 * `HomeBanners` no pueden hacer esto porque ahí la tarjeta lleva además el descarte.
 *
 * El número sale del catálogo cargado, no de una constante que se desactualiza. Sin
 * él, el copy cae en una versión sin números que sigue siendo verdadera.
 */
function CierrePlantillas({
  totalPlantillas,
  onVerTodas,
  hrefVerTodas,
}: {
  /** Cuántas plantillas tiene el catálogo completo. Sin esto, el copy va sin números. */
  totalPlantillas?: number;
  onVerTodas: () => void;
  /** La URL del catálogo, para que la tarjeta sea un enlace de verdad. */
  hrefVerTodas?: string;
}) {
  // El número va REDONDEADO HACIA ABAJO a la decena y con un "+" adelante: "+180" se
  // lee como un tamaño —que es lo que el título quiere transmitir— mientras que "183"
  // se lee como un inventario exacto, y encima envejece con cada plantilla que se
  // suma o se da de baja. Hacia abajo y nunca hacia arriba: el "+" tiene que ser
  // verdadero para cualquier catálogo, no sólo para el de hoy.
  //
  // Por debajo de 20 no hay redondeo que se lea como "muchas" (un "+10" con 19
  // plantillas suena a menos de lo que hay), así que ahí el título va sin número.
  const redondeado =
    totalPlantillas && totalPlantillas >= 20
      ? Math.floor(totalPlantillas / 10) * 10
      : 0;
  const titulo = redondeado
    ? `Mirá las de +${redondeado} plantillas disponibles`
    : 'Mirá todas las plantillas disponibles';
  // La versión corta de mobile: sin el "Mirá las" —a 390px el CTA queda reducido a la
  // flecha y el título tiene que entrar en una línea al lado del ícono—, pero con
  // "disponibles", que es lo que lo hace una frase y no una etiqueta.
  const tituloCorto = redondeado
    ? `+${redondeado} plantillas disponibles`
    : 'Todas las plantillas disponibles';

  return (
    <EnlaceApp
      href={hrefVerTodas}
      onNavegar={onVerTodas}
      // Mismas clases que la tarjeta de los carteles de `HomeBanners`. El literal se
      // repite en vez de importarse porque aquel vive en un componente que esta
      // pantalla no consume, igual que ellos repiten los colores del CTA suave.
      className="group mt-4 relative flex w-full items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4 text-left"
    >
      <span className="flex min-w-0 items-center gap-2">
        <LayoutGrid className="h-4 w-4 shrink-0 text-brand-primary" />
        {/* `truncate`: el número puede crecer y el título no puede empujar al CTA
            fuera de la tarjeta. */}
        <span className="truncate text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
          <span className="sm:hidden">{tituloCorto}</span>
          <span className="hidden sm:inline">{titulo}</span>
        </span>
      </span>
      {/* CTA suave (lavanda + azul de marca) y cuadrado de 36px en mobile, donde queda
          sólo la flecha — ADR-2026-08-04-cta-solo-icono-es-cuadrado. El hover lo
          dispara la tarjeta entera, que es el botón real. */}
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#EBEFFE] text-[14px] font-medium text-brand-primary transition-colors group-hover:bg-[#DFE6FD] sm:h-10 sm:w-auto sm:px-4">
        <span className="hidden sm:inline">Ver todas</span>
        <ChevronRight className="h-4 w-4" />
      </span>
    </EnlaceApp>
  );
}

/**
 * Creaciones recientes + Plantillas + Ediciones rápidas — el cuerpo de la home,
 * COMPARTIDO por las dos versiones (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar).
 *
 * Lo que cambia entre desktop y mobile es lo de ARRIBA: desktop abre con el saludo,
 * los accesos de cuenta y la tira de modos; mobile abre con el hero a sangre y su
 * nav inferior. De acá para abajo las dos muestran lo mismo, y por eso se extrajo
 * en vez de duplicarse: las grillas ya traían sus breakpoints (`grid-cols-2` en
 * teléfono), así que no hubo que adaptar nada.
 *
 * Sigue siendo PRESENTACIONAL, igual que `HomeMinimal`: las plantillas y sus
 * handlers entran por props desde `InterfazV2`. Lo único que lee por su cuenta son
 * las creaciones recientes, que son del usuario y no de la home.
 *
 * Recibe `variante` y no `tema`: `TemaMinimal` es interno de este módulo, así el
 * consumidor de afuera no necesita construir un objeto que no puede nombrar.
 */
export function SeccionesHome({
  idCompany,
  idMarca,
  plantillas,
  totalPlantillas,
  onVerTodasPlantillas,
  hrefTodasPlantillas,
  variante = 'claro',
  sinEdicionesRapidas = false,
  slotDespuesDeRecientes,
}: {
  idCompany: string | null;
  /** Marca activa — la usa "Creaciones recientes" para mostrar sólo lo suyo
   *  (ADR-2026-08-03-creaciones-recientes-por-marca). */
  idMarca: string | null;
  plantillas: FilaPlantillasHome[];
  /** Cuántas plantillas tiene el catálogo completo — el cierre de la sección lo dice
   *  en su título; la home sólo recibe las 3 categorías que rotan y desde ahí ese
   *  total es invisible. */
  totalPlantillas?: number;
  onVerTodasPlantillas: () => void;
  /** La URL del catálogo completo: hace enlaces al "Ver todo" y al cierre de la
   *  sección (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links). */
  hrefTodasPlantillas?: string;
  variante?: VarianteMinimal;
  /**
   * No dibujar "Ediciones rápidas" acá — lo pide la home de desktop
   * (`HomeMinimal.tsx`, ADR-2026-08-31-version2-toma-los-atajos-de-la-version3), que
   * sube esas acciones arriba con otra anatomía; si el pie las dibujara también,
   * saldrían dos veces.
   *
   * Es una omisión y no una mudanza: quien las apaga acá se hace cargo de montarlas.
   * `false` por defecto, así la home de mobile las sigue mostrando al pie.
   */
  sinEdicionesRapidas?: boolean;
  /**
   * Qué va JUSTO DEBAJO de "Creaciones recientes", antes de Plantillas.
   *
   * Existe para la home de desktop, que baja "Ediciones rápidas" a ese hueco
   * (ADR-2026-09-04-ediciones-rapidas-debajo-de-recientes). Es un slot y no un flag
   * porque lo que se monta ahí es de quien lo pide —`HomeMinimal` dibuja los atajos
   * con SU anatomía— y este componente no tiene por qué conocerlo.
   */
  slotDespuesDeRecientes?: ReactNode;
}) {
  const navigate = useNavigate();
  const tema = TEMAS[variante];

  /* Plan para el candado de los atajos — ADR-2026-08-20-el-plan-de-una-seccion-no-puede-venir-del-shell.

     NO sale de `CuentaContext`, aunque el contexto esté ahí y sea lo cómodo. Este
     componente tiene DOS puntos de montaje: dentro de `HomeMinimal` (bajo el provider) y
     directo desde `InterfazV2`, en su rama `sm:hidden` de mobile. En un teléfono el shell
     es `ShellMobile` y `EstiloMinimal` —el único provider de la app— NO se monta, así que
     ese segundo montaje corría con el default del contexto: `plan: ''`, que
     `getModePlanLock` interpreta como "todavía no sé" y no bloquea nada. Resultado: el
     candado existía en desktop y no en mobile, que es justo donde está la mayoría de las
     cuentas gratis.

     Se lee con el patrón canónico del repo (`Modos.tsx`, `SheetModosProducto`,
     `useModosGenerar`): cache de localStorage para el primer paint, valor real después.
     Funciona igual en los dos montajes, sin depender de quién sea el padre. */
  const [planCuenta, setPlanCuenta] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlanCuenta(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  /* A dónde va el upsell. `dentroDelShell` ya existe en el contexto para exactamente
     esto, y su default es `false`, que es lo correcto fuera del shell: `abrirPricing`
     es un no-op ahí, así que usarlo a ciegas convertía el atajo bloqueado en un click
     muerto. Misma bifurcación que ya hace `SheetModosProducto`. */
  const { abrirPricing: abrirPricingCuenta, dentroDelShell } =
    useContext(CuentaContext);
  const abrirPlanes = () =>
    dentroDelShell ? abrirPricingCuenta('bloqueado') : navigate('/subscription');

  return (
    <>
          {/* Creaciones recientes — una sola fila: es un vistazo, no el historial.
              Para verlo entero está "Ver todo". Contenido REAL desde
              ADR-2026-07-31-shell-nuevo-datos-reales; antes eran las imágenes de demo de `contenidoAtlas`. */}
          <CreacionesRecientes idCompany={idCompany} idMarca={idMarca} tema={tema} />

          {slotDespuesDeRecientes}

          {/* Plantillas — una fila por colección REAL (ADR-2026-07-31-inicio-se-funde-interfaz-v2).
              Antes eran 3 categorías sorteadas de un catálogo estático; ahora son las
              colecciones que `InterfazV2` ya tenía cargadas, y clickear una abre su
              sheet en vez de mandar al listado. */}
          {plantillas.length > 0 && (
            <section className="mt-12">
              {/* Igual que "Creaciones recientes": pegado al título y apoyado en su pie. */}
              <div className="flex items-end gap-3">
                <div>
                  {/* En mobile, sólo "Plantillas": la frase entera ocupa dos
                      renglones de los tres que mide la tarjeta antes de la primera
                      miniatura (ADR-2026-08-04-titulo-de-plantillas-corto-en-mobile).
                      El título de la sección no tiene que explicar la sección: eso lo
                      hace la bajada de cada fila. */}
                  <h2 className="text-[22px] font-bold tracking-[-0.01em]">
                    <span className="sm:hidden">Plantillas</span>
                    <span className="hidden sm:inline">
                      Plantillas con escenas ya armadas
                    </span>
                  </h2>
                </div>
                <EnlaceApp
                  href={hrefTodasPlantillas}
                  onNavegar={onVerTodasPlantillas}
                  className={BOTON_VER_TODO}
                >
                  Ver todo <MoveRight className="h-4 w-4" strokeWidth={2.25} />
                </EnlaceApp>
              </div>

              {/* Cada colección en su propia tarjeta, como las de "Ediciones rápidas":
                  así se lee como un bloque cerrado y no como grillas sueltas. */}
              {plantillas.map((fila) => (
                <div
                  key={fila.id}
                  // `overflow-hidden`: la fila de adentro va a sangre hasta el borde
                  // de esta tarjeta, y sin esto el scroll horizontal pintaba las
                  // miniaturas encima del borde y de las esquinas redondeadas
                  // (ADR-2026-08-04-la-fila-se-recorta-en-la-tarjeta).
                  className={`mt-4 overflow-hidden rounded-2xl border border-black/[0.06] p-4 ${tema.tarjeta}`}
                >
                  <div className="mb-3.5">
                    <h3 className="text-[16.5px] font-semibold tracking-[-0.01em]">
                      {fila.titulo}
                    </h3>
                    {/* `hidden sm:block`: en mobile la descripción no entra en una
                        línea y parte la tarjeta en dos renglones de texto antes de
                        que se vea una sola miniatura. El título alcanza para saber
                        qué es la fila; la descripción es contexto de desktop
                        (ADR-2026-08-03-descripciones-plantillas-100-y-fuera-en-mobile). */}
                    {fila.bajada && (
                      <p className="mt-0.5 hidden text-[12.5px] text-black/45 sm:block">
                        {fila.bajada}
                      </p>
                    )}
                  </div>

                  {/* 5 plantillas + "Ver más" como sexta celda: el acceso a la lista
                      completa vive en la grilla, a la misma altura que lo que muestra.
                      La celda "Ver más" sólo aparece si hay MÁS de 5: con 3 items,
                      prometía un resto que no existe. */}
                  <div className={FILA_MINIATURAS}>
                    {fila.items.slice(0, 5).map((it) => (
                      <button
                        key={it.id}
                        onClick={it.onAbrir}
                        className={`group relative aspect-[3/4] overflow-hidden rounded-xl border border-black/[0.06] ${CELDA_MINIATURA} ${tema.hueco}`}
                      >
                        <img
                          src={it.src}
                          alt={it.titulo}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-2.5 pb-2 pt-8 text-left">
                          <span className="block truncate text-[12.5px] font-medium text-white">
                            {it.titulo}
                          </span>
                        </span>
                      </button>
                    ))}

                    {fila.items.length > 5 && (
                      <CeldaVerMas
                        etiqueta={`Ver todas las plantillas de ${fila.titulo}`}
                        onClick={fila.onVerMas}
                        href={fila.hrefVerMas}
                        tema={tema}
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* Cierre de la sección: el "Ver todo" del título queda tres tarjetas
                  arriba, y acá es donde el usuario termina de mirar. */}
              <CierrePlantillas
                totalPlantillas={totalPlantillas}
                onVerTodas={onVerTodasPlantillas}
                hrefVerTodas={hrefTodasPlantillas}
              />
            </section>
          )}

          {/* Ediciones rápidas — cierra la home: son acciones SOBRE lo ya generado,
              así que van después de las creaciones, no antes. */}
          {!sinEdicionesRapidas && (
          <section className="mt-12">
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">Ediciones rápidas</h2>

            {/* En MOBILE, cuatro por fila y sólo ícono + nombre
                (ADR-2026-07-31-home-mobile-filas-horizontales-ediciones-compactas): la tarjeta completa —con su
                descripción y 28px de padding vertical— apilaba SIETE bloques a lo
                largo de la pantalla para cerrar la home. La descripción se
                esconde, no se borra: en desktop hay lugar y es donde se entiende
                qué hace cada acción. */}
            <div className="mt-5 grid grid-cols-4 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              {EDICIONES_RAPIDAS.map((edicion) => {
                const { label, desc, Icono, ruta } = edicion;
                // Candado por plan — ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
                // Hoy sólo lo tiene Animar. Bloqueado NO navega: abre el catálogo de
                // planes, el mismo gesto que la card del modo en la tira y que su ítem
                // en la sidebar.
                const lock = candadoEdicionRapida(edicion, planCuenta);
                return (
                <EnlaceApp
                  key={label}
                  // Bloqueado NO lleva `href`: su click va al catálogo de planes, no al
                  // atajo, así que tampoco tiene que poder abrirse en otra pestaña
                  // (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links).
                  href={lock ? undefined : ruta}
                  onNavegar={() => (lock ? abrirPlanes() : navigate(ruta))}
                  title={lock ? lock.title : undefined}
                  className={`group flex flex-col items-center justify-start rounded-2xl border border-black/[0.06] px-2 py-3 text-center transition-all sm:px-5 sm:py-7 sm:hover:-translate-y-0.5 sm:hover:border-black/[0.1] sm:hover:shadow-[0_8px_28px_rgba(16,24,45,0.07)] ${tema.tarjeta}`}
                >
                  <span
                    className={`mb-1.5 grid h-9 w-9 place-items-center rounded-xl text-[#16181D]/75 transition-colors group-hover:bg-[#EBEDF7] group-hover:text-brand-primary sm:mb-3.5 sm:h-11 sm:w-11 sm:rounded-2xl ${tema.hueco}`}
                  >
                    {lock ? (
                      <span className="material-symbols-rounded text-[18px] sm:text-[21px]">
                        lock
                      </span>
                    ) : (
                      <Icono className="h-4 w-4 sm:h-[19px] sm:w-[19px]" strokeWidth={1.8} />
                    )}
                  </span>
                  <span className="text-[11.5px] font-medium leading-tight sm:text-[14.5px] sm:font-semibold">
                    {label}
                  </span>
                  <span className="mt-1 hidden text-[13px] leading-relaxed text-black/45 sm:block">
                    {desc}
                  </span>
                </EnlaceApp>
                );
              })}
            </div>
          </section>
          )}
    </>
  );
}
